import React, { useState, useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import { WorkoutSession, WorkoutSet, Exercise, SetType } from '@/lib/types';
import {
  createInitialTimedWorkState,
  getRestCompletionTransition,
  getWorkCompletionTransition,
} from '@/lib/timedWorkSequence';
import { getLoginUrl } from '@/const';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Play, Square, Check, RefreshCw, Timer, Plus, Trash2, Award, ChevronRight, ChevronLeft, Volume2, VolumeX, Dumbbell, Flame, Skull, Zap, Link, TrendingDown, AlertTriangle, Target, ChevronDown, ChevronUp, BarChart2, Battery, RotateCcw, X } from 'lucide-react';
import { ALL_SET_TYPES, getSetTypeBadgeConfig } from './SetTypeBadge';
import { toast } from 'sonner';
import BarbellPlateCalculator from './BarbellPlateCalculator';
import ExerciseLibrary from './ExerciseLibrary';
import { PlateSelector } from './PlateSelector';
import { PostSessionFeedbackCard } from './PostSessionFeedbackCard';
import { VoiceMicButton, VoiceResult } from './VoiceMicButton';

interface LiveSessionTrackerProps {
  session: WorkoutSession;
  onComplete: () => void;
  onCancel: () => void;
}

export default function LiveSessionTracker({ session: initialSession, onComplete, onCancel }: LiveSessionTrackerProps) {
  const [session, setSession] = useState<WorkoutSession>(initialSession);

  const utils = trpc.useUtils();
  const { data: rawExercises = [] } = trpc.exercises.list.useQuery();
  const saveSessionMutation = trpc.sessions.save.useMutation({
    onSuccess: () => {
      utils.sessions.list.invalidate();
      utils.sessions.byDate.invalidate();
    },
  });
  const { data: allSessions = [] } = trpc.sessions.list.useQuery();
  const [activeExerciseIndex, setActiveExerciseIndex] = useState(0);
  
  // Timer state
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const [timerPreset, setTimerPreset] = useState(90); // 90s default
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [chimeProfile, setChimeProfile] = useState<'classic' | 'pulse' | 'arpeggio' | 'retro'>(() => {
    return (localStorage.getItem('forge_chime_profile') as any) || 'classic';
  });
  
  // Overall session duration timer
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  
  // Mobile drawers state
  const [showRestDrawer, setShowRestDrawer] = useState(false);
  const [showPlateDrawer, setShowPlateDrawer] = useState(false);
  
  // Exercise library state
  const [showLibrary, setShowLibrary] = useState(false);

  // States for Core/Cardio Ring Timer Station
  const [coreTimerActive, setCoreTimerActive] = useState(false);
  const [coreTimerSeconds, setCoreTimerSeconds] = useState(0);
  const [coreTimerPreset, setCoreTimerPreset] = useState(60);
  const [coreTimerMode, setCoreTimerMode] = useState<'countdown' | 'stopwatch'>('countdown');
  const [coreTimerPhase, setCoreTimerPhase] = useState<'work' | 'rest'>('work');
  const [coreRestSeconds, setCoreRestSeconds] = useState(30);
  const [coreActiveSetId, setCoreActiveSetId] = useState<string | null>(null);
  const [corePendingSetId, setCorePendingSetId] = useState<string | null>(null);
  const coreTimerRef = useRef<NodeJS.Timeout | null>(null);
  const coreCompletionSetIdRef = useRef<string | null>(null);

  // Pulsing rest banner state
  const [showRestBanner, setShowRestBanner] = useState(false);
  const restBannerTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Superset auto-navigate highlight state
  const [supersetHighlight, setSupersetHighlight] = useState(false);
  const supersetHighlightRef = useRef<NodeJS.Timeout | null>(null);

  // Set completion flash animation
  const [completingSetId, setCompletingSetId] = useState<string | null>(null);
  const completingSetRef = useRef<NodeJS.Timeout | null>(null);

  // Set type bottom-sheet picker state
  const [setTypePicker, setSetTypePicker] = useState<{ peId: string; setId: string } | null>(null);

  // Finish workout dialog state
  const [showFinishDialog, setShowFinishDialog] = useState(false);
  const [manualDurationInput, setManualDurationInput] = useState('');  // 'MM:SS' override
  const [showFeedbackCard, setShowFeedbackCard] = useState(false);
  const [completedSessionId, setCompletedSessionId] = useState<string | null>(null);
  // Voice logging — always enabled, no gate
  const { data: userProfile } = trpc.profile.get.useQuery();
  // voiceFlashSetId: set id that should show green flash after voice parse
  const [voiceFlashSetId, setVoiceFlashSetId] = useState<string | null>(null);
  const voiceFlashTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Swipe-to-delete state: tracks swipe offset per set id
  const [swipeState, setSwipeState] = useState<Record<string, number>>({});
  const swipeTouchStartRef = useRef<Record<string, number>>({});

  // Tap-to-edit: id of completed set currently being edited
  const [editingSetId, setEditingSetId] = useState<string | null>(null);

  // Skip exercise dialog state
  const [skipDialog, setSkipDialog] = useState<{ peId: string; exName: string } | null>(null);
  const [skipReasonInput, setSkipReasonInput] = useState('');

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedRef = useRef<NodeJS.Timeout | null>(null);

  // Normalize exercises from server
  const exercises: Exercise[] = rawExercises.map((e) => ({
    id: e.id,
    name: e.name,
    category: e.category,
    notes: e.notes ?? undefined,
    isCustom: e.isCustom,
    trackingMode: e.trackingMode,
    defaultTimerSeconds: e.defaultTimerSeconds ?? undefined,
  }));

  useEffect(() => {
    // Start overall workout duration timer
    elapsedRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (elapsedRef.current) clearInterval(elapsedRef.current);
      if (coreTimerRef.current) clearInterval(coreTimerRef.current);
    };
  }, []);

  // Rest Timer logic
  useEffect(() => {
    if (timerActive && timerSeconds > 0) {
      timerRef.current = setTimeout(() => {
        setTimerSeconds(prev => prev - 1);
      }, 1000);
    } else if (timerActive && timerSeconds === 0) {
      setTimerActive(false);
      triggerTimerEnd();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timerActive, timerSeconds]);



  const playChimeSound = (profile: 'classic' | 'pulse' | 'arpeggio' | 'retro') => {
    if (!soundEnabled) return;
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const now = audioCtx.currentTime;

      if (profile === 'classic') {
        // Classic high-frequency clean beep
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, now);
        gain.gain.setValueAtTime(0.15, now);
        gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.start(now);
        osc.stop(now + 0.4);
      } else if (profile === 'pulse') {
        // Heavy pulse (low-end dual tone, deep bass pulse)
        const freqs = [110, 165]; // A2 and E3 power chord
        freqs.forEach((freq, i) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.type = 'sawtooth'; // heavier texture
          osc.frequency.setValueAtTime(freq, now);
          
          // Apply a lowpass filter to make it a deep sub-bass heavy pulse
          const filter = audioCtx.createBiquadFilter();
          filter.type = 'lowpass';
          filter.frequency.setValueAtTime(300, now);

          gain.gain.setValueAtTime(0.2, now);
          // Pulse twice
          gain.gain.setValueAtTime(0.2, now);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
          gain.gain.setValueAtTime(0.2, now + 0.3);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);

          osc.connect(filter);
          filter.connect(gain);
          gain.connect(audioCtx.destination);
          osc.start(now);
          osc.stop(now + 0.6);
        });
      } else if (profile === 'arpeggio') {
        // Synth arpeggio (C5 -> E5 -> G5 -> C6)
        const notes = [523.25, 659.25, 783.99, 1046.50];
        notes.forEach((freq, index) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(freq, now + index * 0.08);
          gain.gain.setValueAtTime(0, now + index * 0.08);
          gain.gain.linearRampToValueAtTime(0.12, now + index * 0.08 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, now + index * 0.08 + 0.3);
          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.start(now + index * 0.08);
          osc.stop(now + index * 0.08 + 0.3);
        });
      } else if (profile === 'retro') {
        // Retro dual-tone alarm buzzer
        const freqs = [587.33, 622.25]; // D5 and D#5 dissonant buzzer
        freqs.forEach((freq) => {
          const osc = audioCtx.createOscillator();
          const gain = audioCtx.createGain();
          osc.type = 'triangle';
          osc.frequency.setValueAtTime(freq, now);
          
          // Fast volume modulation to sound like a buzzer
          gain.gain.setValueAtTime(0.1, now);
          gain.gain.linearRampToValueAtTime(0.1, now + 0.1);
          gain.gain.setValueAtTime(0, now + 0.12);
          gain.gain.setValueAtTime(0.1, now + 0.15);
          gain.gain.linearRampToValueAtTime(0.1, now + 0.25);
          gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);

          osc.connect(gain);
          gain.connect(audioCtx.destination);
          osc.start(now);
          osc.stop(now + 0.4);
        });
      }
    } catch (e) {
      console.warn("Chime playback failed:", e);
    }
  };

  const triggerCoreTimerEnd = () => {
    toast.success("Core set complete! Excellent hold.", {
      duration: 5000,
    });
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }
    playChimeSound(chimeProfile);
  };

  const triggerCoreTimerEndRest = () => {
    toast.success("Rest complete — go time!", { duration: 3000 });
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([100, 50, 100]);
    }
    playChimeSound(chimeProfile);
  };

  const triggerTimerEnd = () => {
    toast.success("Rest period complete! Next set.", {
      duration: 5000,
    });
    
    // Haptic vibration alert (200ms pulse, 100ms pause, 200ms pulse)
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }

    playChimeSound(chimeProfile);
  };

  const playBeep = (frequency = 880, duration = 0.1) => {
    if (!soundEnabled) return;
    try {
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      oscillator.start();
      oscillator.stop(audioCtx.currentTime + duration);
    } catch (e) {
      console.warn("Audio synthesis failed:", e);
    }
  };

  const handleStartRestTimer = (seconds: number = timerPreset) => {
    setTimerSeconds(seconds);
    setTimerActive(true);
  };

  const handleToggleSet = (peId: string, setId: string) => {
    let wasCompleted = false;
    const updatedPlanned = session.plannedExercises.map(pe => {
      if (pe.id !== peId) return pe;
      return {
        ...pe,
        sets: pe.sets.map(s => {
          if (s.id !== setId) return s;
          const nextState = !s.completed;
          wasCompleted = nextState;
          
          // Validate set inputs
          const exercise = exercises.find(e => e.id === pe.exerciseId);
          if (nextState) {
            if (exercise?.trackingMode === 'time') {
              if (s.durationSeconds === undefined) {
                toast.error("Please fill in duration before completing the set!");
                return s;
              }
            } else {
              if (s.weight === undefined || s.reps === undefined) {
                toast.error("Please fill in weight and reps before completing the set!");
                return s;
              }
            }
          }
          return { ...s, completed: nextState };
        })
      };
    });

    // Check if the validation failed and blocked the update
    const isUnchanged = JSON.stringify(session.plannedExercises) === JSON.stringify(updatedPlanned);
    if (isUnchanged) return;

    setSession({
      ...session,
      plannedExercises: updatedPlanned
    });

    if (wasCompleted) {
      toast.success("Set logged!");

      // Trigger brief scale+glow flash on the completed row
      if (completingSetRef.current) clearTimeout(completingSetRef.current);
      setCompletingSetId(setId);
      completingSetRef.current = setTimeout(() => setCompletingSetId(null), 600);

      // Prompt-30: rest timer logic for child block sets
      const currentPEForDrop = updatedPlanned.find(pe => pe.id === peId);
      const completedSet = currentPEForDrop?.sets.find(s => s.id === setId);

      // Legacy drop_set: no rest between entries
      if (completedSet?.setType === 'drop_set') {
        return;
      }

      // Prompt-30: child block sets (Drop/Cluster/Mech children)
      // No rest between child blocks — only fire rest timer on the LAST child in the block
      if (completedSet?.isChildBlock && completedSet.parentSetId) {
        const allSetsForPE = currentPEForDrop?.sets ?? [];
        const siblingChildren = allSetsForPE.filter(
          s => s.parentSetId === completedSet.parentSetId
        );
        const lastChild = siblingChildren[siblingChildren.length - 1];
        if (lastChild && lastChild.id !== setId) {
          // Not the last child — no rest timer, just flash
          return;
        }
        // This IS the last child — fall through to fire rest timer below
      }

      // Cluster micro-rest: between parent and first child, fire a SHORT rest
      if (completedSet?.setType === 'cluster' && !completedSet.isChildBlock && completedSet.microRestSeconds) {
        // Parent cluster completed — start micro-rest countdown
        // We use the existing rest timer but with a short preset
        // (The full rest timer will fire after the last child)
        return; // Skip full rest timer for parent cluster row
      }

      // Superset auto-navigate: if this exercise is paired, jump to the partner immediately
      const currentPE = updatedPlanned.find(pe => pe.id === peId);
      if (currentPE?.supersetWithId) {
        const partnerIndex = updatedPlanned.findIndex(pe => pe.id === currentPE.supersetWithId);
        if (partnerIndex !== -1) {
          const partnerEx = exercises.find(e => e.id === updatedPlanned[partnerIndex]!.exerciseId);
          setActiveExerciseIndex(partnerIndex);
          // Flash highlight on the partner exercise header
          if (supersetHighlightRef.current) clearTimeout(supersetHighlightRef.current);
          setSupersetHighlight(true);
          supersetHighlightRef.current = setTimeout(() => setSupersetHighlight(false), 1800);
          toast(`⚡ SUPERSET → ${partnerEx?.name ?? 'Next exercise'} — GO!`, {
            duration: 2500,
            style: { background: 'rgba(249,115,22,0.15)', border: '1px solid rgba(249,115,22,0.5)', color: 'rgb(251,146,60)' },
          });
          // Don't start rest timer on superset transition — no rest between paired exercises
          return;
        }
      }

      handleStartRestTimer();
      // Show pulsing rest banner for 8 seconds
      setShowRestBanner(true);
      if (restBannerTimeoutRef.current) clearTimeout(restBannerTimeoutRef.current);
      restBannerTimeoutRef.current = setTimeout(() => setShowRestBanner(false), 8000);
    }
  };

  const handleSetChange = (peId: string, setId: string, field: keyof WorkoutSet, value: any) => {
    const updated = {
      ...session,
      plannedExercises: session.plannedExercises.map(pe => {
        if (pe.id !== peId) return pe;
        return {
          ...pe,
          sets: pe.sets.map(s => {
            if (s.id !== setId) return s;
            return { ...s, [field]: value };
          })
        };
      })
    };
    setSession(updated);
    // Auto-save on every edit so no data is lost on phone lock / refresh
    saveSessionMutation.mutate(updated);
  };

  // ── Skip exercise ────────────────────────────────────────────────────────
  const handleSkipExercise = (peId: string, reason: string) => {
    const updated = {
      ...session,
      plannedExercises: session.plannedExercises.map(pe =>
        pe.id === peId ? { ...pe, skipped: true, skipReason: reason || 'Too fatigued' } : pe
      )
    };
    setSession(updated);
    saveSessionMutation.mutate(updated);
    setSkipDialog(null);
    setSkipReasonInput('');
    toast.success('Exercise skipped — noted in session record');
  };

  const handleUnskipExercise = (peId: string) => {
    const updated = {
      ...session,
      plannedExercises: session.plannedExercises.map(pe =>
        pe.id === peId ? { ...pe, skipped: false, skipReason: undefined } : pe
      )
    };
    setSession(updated);
    saveSessionMutation.mutate(updated);
    toast.success('Exercise restored');
  };

  // ── Helper: is this set type a multi-block parent type? ─────────────────
  const isMultiBlockParent = (t: SetType) =>
    ['drop', 'cluster', 'mechanical', 'feeder', 'back_off'].includes(t);

  // ── Helper: compute effective volume lbs for a set (parent perspective) ──
  // Used by Telemetry. Feeder = 0; child blocks don't add; parent = weight*reps.
  const isFeederType = (t?: SetType) => t === 'feeder';

  const handleAddSet = (peId: string, type: SetType = 'normal') => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map(pe => {
        if (pe.id !== peId) return pe;

        const exercise = exercises.find(e => e.id === pe.exerciseId);
        const isTime = exercise?.trackingMode === 'time';
        const nonChildSets = pe.sets.filter(s => !s.isChildBlock);
        const firstWorkingSet = nonChildSets.find(s => s.setType !== 'warmup' && !s.isWarmup && s.setType !== 'feeder');
        const lastWorkingSet = [...nonChildSets].reverse().find(s =>
          s.setType !== 'warmup' && !s.isWarmup && s.setType !== 'feeder'
        ) ?? nonChildSets[nonChildSets.length - 1];
        const baseW = lastWorkingSet?.weight;
        const baseR = lastWorkingSet?.reps ?? 8;
        const baseDur = lastWorkingSet?.durationSeconds ?? 60;
        const ts = Date.now();
        const snap = (w: number) => Math.round(w / 2.5) * 2.5;

        // ── WARMUP (legacy) ──────────────────────────────────────────────────
        if (type === 'warmup') {
          const bw = firstWorkingSet?.weight ?? baseW;
          const wu1: WorkoutSet = {
            id: `set-${ts}-wu1`, completed: false, setType: 'warmup', isWarmup: true,
            weight: bw ? snap(bw * 0.5) : undefined,
            reps: isTime ? undefined : 8,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const wu2: WorkoutSet = {
            id: `set-${ts + 1}-wu2`, completed: false, setType: 'warmup', isWarmup: true,
            weight: bw ? snap(bw * 0.75) : undefined,
            reps: isTime ? undefined : 5,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const existingWarmups = pe.sets.filter(s => s.setType === 'warmup' || s.isWarmup);
          const workingSets = pe.sets.filter(s => s.setType !== 'warmup' && !s.isWarmup);
          return { ...pe, sets: [...existingWarmups, wu1, wu2, ...workingSets] };
        }

        // ── KILL (legacy) ────────────────────────────────────────────────────
        if (type === 'kill') {
          const killW = baseW ? snap(baseW * 0.75) : undefined;
          const killSet: WorkoutSet = {
            id: `set-${ts}-kill`, completed: false, setType: 'kill',
            weight: killW, reps: undefined,
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, killSet] };
        }

        // ── FEEDER: 1-2 warm-up rows BEFORE working sets, countsAsVolume=false ─
        if (type === 'feeder') {
          const feeder1: WorkoutSet = {
            id: `set-${ts}-f1`, completed: false, setType: 'feeder',
            childLabel: 'F1', countsAsVolume: false,
            weight: baseW ? snap(baseW * 0.4) : undefined,
            reps: isTime ? undefined : 15,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const feeder2: WorkoutSet = {
            id: `set-${ts + 1}-f2`, completed: false, setType: 'feeder',
            childLabel: 'F2', countsAsVolume: false,
            weight: baseW ? snap(baseW * 0.6) : undefined,
            reps: isTime ? undefined : 10,
            durationSeconds: isTime ? baseDur : undefined,
          };
          // Insert feeders before any non-feeder, non-warmup sets
          const feeders = pe.sets.filter(s => s.setType === 'feeder' || s.setType === 'warmup' || s.isWarmup);
          const rest = pe.sets.filter(s => s.setType !== 'feeder' && s.setType !== 'warmup' && !s.isWarmup);
          return { ...pe, sets: [...feeders, feeder1, feeder2, ...rest] };
        }

        // ── BACK-OFF: 1 row AFTER working sets at 65% ───────────────────────
        if (type === 'back_off') {
          const backOff: WorkoutSet = {
            id: `set-${ts}-bo`, completed: false, setType: 'back_off',
            childLabel: 'B', countsAsVolume: true,
            weight: baseW ? snap(baseW * 0.65) : undefined,
            reps: isTime ? undefined : (baseR ? baseR + 4 : 12),
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, backOff] };
        }

        // ── DROP: parent + 2 child rows (D1 at 80%, D2 at 60%) ──────────────
        if (type === 'drop') {
          const parentId = `set-${ts}-drop`;
          const parent: WorkoutSet = {
            id: parentId, completed: false, setType: 'drop',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
          };
          const child1: WorkoutSet = {
            id: `set-${ts + 1}-d1`, completed: false, setType: 'drop',
            parentSetId: parentId, isChildBlock: true, childLabel: 'D1',
            weight: baseW ? snap(baseW * 0.8) : undefined,
            reps: isTime ? undefined : (baseR ? baseR + 2 : 10),
            durationSeconds: isTime ? baseDur : undefined,
          };
          const child2: WorkoutSet = {
            id: `set-${ts + 2}-d2`, completed: false, setType: 'drop',
            parentSetId: parentId, isChildBlock: true, childLabel: 'D2',
            weight: baseW ? snap(baseW * 0.6) : undefined,
            reps: isTime ? undefined : (baseR ? baseR + 4 : 12),
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, parent, child1, child2] };
        }

        // ── CLUSTER: parent + 2 child rows, same weight, 15s micro-rest ─────
        if (type === 'cluster') {
          const parentId = `set-${ts}-cluster`;
          const clusterReps = Math.max(3, Math.round((baseR ?? 5) * 0.6));
          const parent: WorkoutSet = {
            id: parentId, completed: false, setType: 'cluster',
            weight: baseW, reps: isTime ? undefined : clusterReps,
            durationSeconds: isTime ? baseDur : undefined,
            microRestSeconds: 15,
          };
          const child1: WorkoutSet = {
            id: `set-${ts + 1}-c1`, completed: false, setType: 'cluster',
            parentSetId: parentId, isChildBlock: true, childLabel: 'C1',
            weight: baseW, reps: isTime ? undefined : clusterReps,
            durationSeconds: isTime ? baseDur : undefined,
            microRestSeconds: 15,
          };
          const child2: WorkoutSet = {
            id: `set-${ts + 2}-c2`, completed: false, setType: 'cluster',
            parentSetId: parentId, isChildBlock: true, childLabel: 'C2',
            weight: baseW, reps: isTime ? undefined : clusterReps,
            durationSeconds: isTime ? baseDur : undefined,
          };
          return { ...pe, sets: [...pe.sets, parent, child1, child2] };
        }

        // ── MECHANICAL DROP: parent + 2 child rows, same weight, variation note ─
        if (type === 'mechanical') {
          const parentId = `set-${ts}-mech`;
          const parent: WorkoutSet = {
            id: parentId, completed: false, setType: 'mechanical',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
            mechVariation: 'Variation 1',
          };
          const child1: WorkoutSet = {
            id: `set-${ts + 1}-m1`, completed: false, setType: 'mechanical',
            parentSetId: parentId, isChildBlock: true, childLabel: 'M1',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
            mechVariation: 'Variation 2',
          };
          const child2: WorkoutSet = {
            id: `set-${ts + 2}-m2`, completed: false, setType: 'mechanical',
            parentSetId: parentId, isChildBlock: true, childLabel: 'M2',
            weight: baseW, reps: isTime ? undefined : baseR,
            durationSeconds: isTime ? baseDur : undefined,
            mechVariation: 'Variation 3',
          };
          return { ...pe, sets: [...pe.sets, parent, child1, child2] };
        }

        // ── DROP_SET (legacy) — keep old dropSetEntries model for backward compat ─
        if (type === 'drop_set') {
          const buildDropEntries = (bw?: number) => {
            if (!bw) return [];
            return [
              { weight: bw, reps: baseR },
              { weight: snap(bw * 0.8), reps: baseR + 2 },
              { weight: snap(bw * 0.6), reps: baseR + 4 },
            ];
          };
          const legacyDrop: WorkoutSet = {
            id: `set-${ts}-${pe.sets.length}`, completed: false, setType: 'drop_set',
            weight: baseW, reps: undefined,
            durationSeconds: isTime ? baseDur : undefined,
            dropSetEntries: buildDropEntries(baseW),
          };
          return { ...pe, sets: [...pe.sets, legacyDrop] };
        }

        // ── All other types: single row ──────────────────────────────────────
        const newSet: WorkoutSet = {
          id: `set-${ts}-${pe.sets.length}`,
          reps: isTime ? undefined : (type === 'amrap' ? undefined : baseR),
          weight: baseW,
          durationSeconds: isTime ? baseDur : undefined,
          completed: false,
          setType: type,
          isWarmup: false,
          isFailure: type === 'failure' ? false : undefined,
          isAmrap: type === 'amrap' ? true : undefined,
        };
        return { ...pe, sets: [...pe.sets, newSet] };
      })
    });
  };

  const handleAutoCompleteAllSets = (peId: string) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map(pe => {
        if (pe.id !== peId) return pe;
        
        const exercise = exercises.find(e => e.id === pe.exerciseId);
        const updatedSets = pe.sets.map(set => {
          if (exercise?.trackingMode === 'time') {
            return {
              ...set,
              durationSeconds: set.durationSeconds ?? 60,
              weight: set.weight, // weight can remain undefined (bodyweight)
              completed: true
            };
          } else {
            return {
              ...set,
              weight: set.weight ?? 135,
              reps: set.reps ?? 10,
              completed: true
            };
          }
        });

        return {
          ...pe,
          sets: updatedSets
        };
      })
    });

    toast.success("⚡ All sets marked complete!", {
      icon: <Check className="h-4 w-4 text-success" />
    });
    
    // Start rest timer automatically
    handleStartRestTimer();
  };

  const handleAddExerciseOnTheFly = (exercise: Exercise) => {
    // Check if exercise already added
    if (session.plannedExercises.some(pe => pe.exerciseId === exercise.id)) {
      toast.warning(`"${exercise.name}" is already in this session`);
      return;
    }

    const defaultSet: WorkoutSet = {
      id: `set-${Date.now()}-0`,
      reps: exercise.trackingMode === 'time' ? undefined : undefined,
      weight: undefined,
      durationSeconds: exercise.trackingMode === 'time' ? 60 : undefined,
      completed: false
    };

    const newPlanned = {
      id: `pe-${Date.now()}`,
      exerciseId: exercise.id,
      sets: [defaultSet]
    };

    const updatedSession = {
      ...session,
      plannedExercises: [...session.plannedExercises, newPlanned]
    };

    setSession(updatedSession);
    saveSessionMutation.mutate(updatedSession); // Persist live state to db
    
    // Set the active index to the newly added exercise
    setActiveExerciseIndex(updatedSession.plannedExercises.length - 1);
    setShowLibrary(false);
    toast.success(`Added "${exercise.name}" on the fly!`);
  };

  const handleRemoveExerciseOnTheFly = (peId: string, exerciseName: string) => {
    const targetPE = session.plannedExercises.find(pe => pe.id === peId);
    if (!targetPE) return;

    const hasLoggedSets = targetPE.sets.some(s => s.completed);
    if (hasLoggedSets) {
      if (!confirm(`Are you sure you want to remove "${exerciseName}"? You have already logged completed sets for this exercise, and removing it will discard those sets.`)) {
        return;
      }
    }

    const updatedPlanned = session.plannedExercises.filter(pe => pe.id !== peId);
    const updatedSession = {
      ...session,
      plannedExercises: updatedPlanned
    };

    setSession(updatedSession);
    saveSessionMutation.mutate(updatedSession); // Persist live state to db

    // Adjust active index so it doesn't point out of bounds
    if (activeExerciseIndex >= updatedPlanned.length) {
      setActiveExerciseIndex(Math.max(0, updatedPlanned.length - 1));
    }
    toast.success(`Removed "${exerciseName}"`);
  };

  const handleRemoveSet = (peId: string, setId: string) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map(pe => {
        if (pe.id !== peId) return pe;
        if (pe.sets.length <= 1) return pe;

        const targetSet = pe.sets.find(s => s.id === setId);
        if (!targetSet) return pe;

        let idsToRemove = new Set<string>([setId]);

        // Prompt-30 edge case: if deleting a PARENT set, also remove all its children
        if (!targetSet.isChildBlock) {
          pe.sets.forEach(s => {
            if (s.parentSetId === setId) idsToRemove.add(s.id);
          });
        }

        // Prompt-30 edge case: if deleting a CHILD block, also remove sibling children
        // and the parent if it has no remaining children (i.e., whole block gone)
        if (targetSet.isChildBlock && targetSet.parentSetId) {
          const parentId = targetSet.parentSetId;
          const siblings = pe.sets.filter(s => s.parentSetId === parentId && s.id !== setId);
          // If this is the last child, remove the parent too
          if (siblings.length === 0) {
            idsToRemove.add(parentId);
          }
          // Always remove the clicked child
          idsToRemove.add(setId);
        }

        const newSets = pe.sets.filter(s => !idsToRemove.has(s.id));
        // Guard: never leave 0 sets
        if (newSets.length === 0) return pe;
        return { ...pe, sets: newSets };
      })
    });
  };

  // Prompt-30: revert a set type back to 'normal' (Straight), removing child blocks
  const handleRevertSetToStraight = (peId: string, setId: string) => {
    setSession({
      ...session,
      plannedExercises: session.plannedExercises.map(pe => {
        if (pe.id !== peId) return pe;
        const targetSet = pe.sets.find(s => s.id === setId);
        if (!targetSet) return pe;
        // Remove children of this set
        const idsToRemove = new Set<string>(
          pe.sets.filter(s => s.parentSetId === setId).map(s => s.id)
        );
        const newSets = pe.sets
          .filter(s => !idsToRemove.has(s.id))
          .map(s => s.id === setId ? {
            ...s,
            setType: 'normal' as const,
            isChildBlock: undefined,
            parentSetId: undefined,
            childLabel: undefined,
            microRestSeconds: undefined,
            mechVariation: undefined,
            dropSetEntries: undefined,
          } : s);
        return { ...pe, sets: newSets };
      })
    });
  };

  // Handle voice result from VoiceMicButton
  const handleVoiceResult = (result: VoiceResult) => {
    if (result.mode !== 'session') return;
    const parsed = result.parsed as any;
    if (!parsed || !activePlannedExercise) return;

    // Handle special commands
    if (parsed.command === 'next_exercise') {
      if (activeExerciseIndex < session.plannedExercises.length - 1) {
        setActiveExerciseIndex(prev => prev + 1);
        toast.success('⏭ Next exercise', { duration: 1500 });
      }
      return;
    }
    if (parsed.command === 'finish_workout') {
      handleFinishWorkout();
      return;
    }

    // Find the first incomplete set to fill
    const incompleteSet = activePlannedExercise.sets.find(s => !s.completed);
    if (!incompleteSet) {
      toast.info('All sets completed for this exercise', { duration: 2000 });
      return;
    }

    // Apply parsed values to the incomplete set
    const updates: Partial<WorkoutSet> = {};
    if (parsed.weight !== undefined) updates.weight = parsed.weight;
    if (parsed.reps !== undefined) updates.reps = parsed.reps;
    if (parsed.rpe !== undefined) (updates as any).rpe = parsed.rpe;
    if (parsed.rir !== undefined) (updates as any).rir = parsed.rir;
    if (parsed.isFailure) (updates as any).isFailure = true;

    // Apply all updates at once
    setSession(prev => ({
      ...prev,
      plannedExercises: prev.plannedExercises.map(pe => {
        if (pe.id !== activePlannedExercise.id) return pe;
        return {
          ...pe,
          sets: pe.sets.map(s => {
            if (s.id !== incompleteSet.id) return s;
            return { ...s, ...updates };
          }),
        };
      }),
    }));

    // Build confirmation message
    const parts: string[] = [];
    if (parsed.weight !== undefined) parts.push(`${parsed.weight} lbs`);
    if (parsed.reps !== undefined) parts.push(`${parsed.reps} reps`);
    if (parsed.rpe !== undefined) parts.push(`RPE ${parsed.rpe}`);
    if (parsed.rir !== undefined) parts.push(`RIR ${parsed.rir}`);
    if (parsed.isFailure) parts.push('failure');

    if (parts.length > 0) {
      toast.success(`🎙 Set ${activePlannedExercise.sets.indexOf(incompleteSet) + 1}: ${parts.join(' · ')}`, {
        duration: 2000,
      });
      // Green flash on inputs for 1.5s
      if (voiceFlashTimerRef.current) clearTimeout(voiceFlashTimerRef.current);
      setVoiceFlashSetId(incompleteSet.id);
      voiceFlashTimerRef.current = setTimeout(() => setVoiceFlashSetId(null), 1500);
    } else {
      toast.warning('🎙 Heard you, but couldn\'t parse the set — try again', { duration: 2500 });
    }
  };

  const handleFinishWorkout = () => {
    // Open the finish dialog — user can override duration before confirming
    setManualDurationInput(formatDuration(elapsedSeconds));
    setShowFinishDialog(true);
  };

  const handleConfirmFinish = async () => {
    setShowFinishDialog(false);

    // Parse manual duration override (MM:SS or M:SS)
    let durationSecs = elapsedSeconds;
    const match = manualDurationInput.match(/^(\d+):([0-5]\d)$/);
    if (match) {
      durationSecs = parseInt(match[1]) * 60 + parseInt(match[2]);
    }

    const completedSession: WorkoutSession = {
      ...session,
      completedAt: Date.now(),
      isLive: false,
      notes: `${session.notes || ''}\nDuration: ${formatDuration(durationSecs)}`.trim()
    };

    // ── Safety net: save to localStorage BEFORE the network call ──────────
    // If the network save fails (auth expired, offline, etc.), the session
    // data is preserved and can be retried.
    const BACKUP_KEY = `session_backup_${completedSession.id}`;
    try {
      localStorage.setItem(BACKUP_KEY, JSON.stringify(completedSession));
    } catch (_) { /* localStorage full — non-fatal */ }

    try {
      await saveSessionMutation.mutateAsync(completedSession);
      // Clear backup on success
      try { localStorage.removeItem(BACKUP_KEY); } catch (_) {}
      toast.success("🔥 Workout Completed! Awesome job, Ross.");
      // Show post-session feedback card before returning to dashboard
      setCompletedSessionId(completedSession.id);
      setShowFeedbackCard(true);
    } catch (err: any) {
      console.error('[Finish Workout] Save error:', err);
      // Detect auth failure specifically
      const isAuthError = err?.message?.includes('10001') ||
        err?.message?.includes('UNAUTHORIZED') ||
        err?.message?.includes('Missing session') ||
        err?.data?.code === 'UNAUTHORIZED';

      // ── CRITICAL: Release the nav lock regardless of save outcome ──────────
      // The localStorage backup is already written above. The recovery banner
      // in Home.tsx will handle the retry. Do NOT trap the user in the session.
      // Call onComplete() BEFORE showing the toast so the session is cleared
      // synchronously — this prevents the global UNAUTHORIZED redirect in
      // main.tsx from racing with the nav release.
      onComplete();

      if (isAuthError) {
        toast.error(
          '⚠️ Session expired — your workout is saved locally. Tap to log back in.',
          {
            duration: 10000,
            action: {
              label: 'Log In',
              onClick: () => { window.location.href = getLoginUrl(); }
            }
          }
        );
      } else {
        toast.error(
          '⚠️ Save failed — your workout data is backed up locally. Open the app to retry.',
          { duration: 10000 }
        );
      }
    }
  };

  const formatDuration = (totalSecs: number) => {
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const activePlannedExercise = session.plannedExercises[activeExerciseIndex];
  const activeExercise = exercises.find(e => e.id === activePlannedExercise?.exerciseId);

  // Sync core timer defaults when active exercise changes
  useEffect(() => {
    if (activeExercise && activeExercise.trackingMode === 'time') {
      const defaultPreset = activeExercise.defaultTimerSeconds || 60;
      setCoreTimerMode('countdown');
      coreCompletionSetIdRef.current = null;
      const initialTimedWork = createInitialTimedWorkState(
        activePlannedExercise?.sets ?? [],
        defaultPreset
      );
      setCoreTimerPreset(initialTimedWork.preset);
      setCoreTimerSeconds(initialTimedWork.seconds);
      setCoreTimerActive(initialTimedWork.timerActive);
      setCoreTimerPhase(initialTimedWork.phase);
      setCorePendingSetId(initialTimedWork.pendingSetId);
      setCoreActiveSetId(initialTimedWork.activeSetId);
    }
  }, [activeExerciseIndex, activeExercise?.id]);

  const completeTimedWorkSet = () => {
    if (!coreActiveSetId || !activePlannedExercise) return false;
    if (coreCompletionSetIdRef.current === coreActiveSetId) return false;

    const activeSet = activePlannedExercise.sets.find((set) => set.id === coreActiveSetId);
    if (!activeSet || activeSet.completed) return false;

    // Lock before state updates so repeat effects/callbacks cannot double-log.
    coreCompletionSetIdRef.current = coreActiveSetId;
    setSession((previousSession) => ({
      ...previousSession,
      plannedExercises: previousSession.plannedExercises.map((plannedExercise) =>
        plannedExercise.id !== activePlannedExercise.id
          ? plannedExercise
          : {
              ...plannedExercise,
              sets: plannedExercise.sets.map((set) =>
                set.id === coreActiveSetId
                  ? { ...set, durationSeconds: coreTimerPreset, completed: true }
                  : set
              ),
            }
      ),
    }));

    triggerCoreTimerEnd();
    if (completingSetRef.current) clearTimeout(completingSetRef.current);
    setCompletingSetId(coreActiveSetId);
    completingSetRef.current = setTimeout(() => setCompletingSetId(null), 600);
    return true;
  };

  // Core/Cardio Timer Station logic. This owns the timed-work sequence:
  // manual first start → work completion → rest → next work, until final set.
  useEffect(() => {
    if (!coreTimerActive) return;

    coreTimerRef.current = setTimeout(() => {
      if (coreTimerMode === 'stopwatch') {
        setCoreTimerSeconds((seconds) => seconds + 1);
        return;
      }

      if (coreTimerSeconds > 1) {
        if (coreTimerSeconds <= 4) playBeep(440, 0.08);
        setCoreTimerSeconds((seconds) => seconds - 1);
        return;
      }

      if (coreTimerPhase === 'rest') {
        const transition = getRestCompletionTransition(
          activePlannedExercise?.sets ?? [],
          corePendingSetId
        );

        setCorePendingSetId(null);
        setCoreTimerPhase('work');

        if (transition.kind === 'finished') {
          setCoreTimerActive(false);
          setCoreActiveSetId(null);
          setCoreTimerSeconds(0);
          return;
        }

        triggerCoreTimerEndRest();
        coreCompletionSetIdRef.current = null;
        const workDuration =
          transition.nextSet.durationSeconds || activeExercise?.defaultTimerSeconds || 60;
        setCoreActiveSetId(transition.nextSet.id);
        setCoreTimerPreset(workDuration);
        setCoreTimerSeconds(workDuration);
        return;
      }

      if (!completeTimedWorkSet() || !activePlannedExercise || !coreActiveSetId) {
        setCoreTimerActive(false);
        return;
      }

      const transition = getWorkCompletionTransition(
        activePlannedExercise.sets,
        coreActiveSetId
      );

      if (transition.kind !== 'rest') {
        setCoreTimerActive(false);
        setCorePendingSetId(null);
        setCoreActiveSetId(null);
        setCoreTimerPhase('work');
        setCoreTimerSeconds(0);
        return;
      }

      setCorePendingSetId(transition.pendingSetId);
      setCoreTimerPhase('rest');
      setCoreTimerSeconds(coreRestSeconds);
    }, 1000);

    return () => {
      if (coreTimerRef.current) clearTimeout(coreTimerRef.current);
    };
  }, [coreTimerActive, coreTimerMode, coreTimerSeconds, coreActiveSetId, coreTimerPreset, coreRestSeconds, coreTimerPhase, corePendingSetId, activePlannedExercise]);

  // Dynamic Rest recommendation based on exercise category
  const getRecommendedRest = (category?: string): number => {
    if (!category) return 90;
    const cat = category.toLowerCase();
    if (cat === 'legs' || cat === 'back') return 180; // Compound multi-joint lifts need more rest
    if (cat === 'chest' || cat === 'shoulders') return 120; // Pressing variations
    return 90; // Arms, core, cardio, other
  };

  const recommendedRest = getRecommendedRest(activeExercise?.category);

  // Get previous session history for active exercise
  const getPreviousSessionHistory = (): { date: string; sets: { reps?: number; weight?: number; durationSeconds?: number; setType?: string; isFailure?: boolean; isAmrap?: boolean; dropSetEntries?: { weight: number; reps: number }[]; rpe?: number; rir?: number }[] } | null => {
    if (!activeExercise) return null;
    const completedSessions = allSessions.filter((s) => s.completedAt);
    completedSessions.sort((a, b) => b.date.localeCompare(a.date));

    for (const s of completedSessions) {
      const matchedPE = (s.plannedExercises as any[]).find((pe: any) => pe.exerciseId === activeExercise.id);
      if (matchedPE) {
        // Accept sets from completed sessions — if the session has completedAt, trust sets with data
        const completedSets = (matchedPE.sets as any[]).filter((set: any) => {
          if (set.setType === 'warmup' || set.isWarmup) return false; // never show warmups in history
          if (activeExercise.trackingMode === 'time') {
            return (set.completed || s.completedAt) && set.durationSeconds !== undefined;
          } else {
            return (set.completed || s.completedAt) && set.weight !== undefined && set.reps !== undefined;
          }
        });
        if (completedSets.length > 0) {
          return {
            date: s.date,
            sets: completedSets.map((set: any) => ({
              reps: set.reps,
              weight: set.weight,
              durationSeconds: set.durationSeconds,
              setType: set.setType,
              isFailure: set.isFailure,
              isAmrap: set.isAmrap,
              dropSetEntries: set.dropSetEntries,
              rpe: set.rpe,
              rir: set.rir,
            })),
          };
        }
      }
    }
    return null;
  };

  const previousHistory = getPreviousSessionHistory();

  // ── Auto rest duration by set type / training mode ──────────────────────
  const getAutoRestSeconds = (set: WorkoutSet): number => {
    const t = set.setType;
    if (t === 'feeder') return 0; // no timer for feeder sets
    if (t === 'drop' || t === 'drop_set') return 20;
    if (t === 'cluster') return 20;
    if (t === 'amrap' || t === 'failure') return 180;
    if (t === 'warmup' || set.isWarmup) return 30;
    // Mode-aware defaults for working sets
    const mode = (session as any).trackingMode ?? 'overload';
    if (mode === 'hypertrophy') return 75;
    if (mode === 'deload') return 60;
    return recommendedRest; // overload — category-based
  };

  // ── Collapsed exercise list state ────────────────────────────────────────
  // null = only active exercise expanded; string = that exercise's peId is expanded
  const [expandedExerciseId, setExpandedExerciseId] = useState<string | null>(null);

  // ── Session-wide set counts ──────────────────────────────────────────────
  const totalSetsCount = session.plannedExercises.reduce((acc, pe) =>
    acc + pe.sets.filter(s => !s.isChildBlock && s.setType !== 'feeder').length, 0);
  const completedSetsCount = session.plannedExercises.reduce((acc, pe) =>
    acc + pe.sets.filter(s => !s.isChildBlock && s.setType !== 'feeder' && s.completed).length, 0);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#1a1a1a', color: '#f0f0f0' }}>
      {/* ── TOP BAR ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-white/10">
        {/* Left: session name + live indicator */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-2 h-2 rounded-full bg-destructive animate-pulse shrink-0" />
          <span className="font-mono text-xs font-bold text-foreground uppercase truncate max-w-[180px]">
            {session.name}
          </span>
          {(() => {
            const mode = (session as any).trackingMode ?? 'overload';
            if (mode === 'hypertrophy') return <span className="text-[8px] font-mono font-bold bg-emerald-400/10 text-emerald-400 border border-emerald-400/30 px-1.5 py-0.5 rounded-sm uppercase tracking-wider shrink-0">HY</span>;
            if (mode === 'deload') return <span className="text-[8px] font-mono font-bold bg-blue-400/10 text-blue-400 border border-blue-400/30 px-1.5 py-0.5 rounded-sm uppercase tracking-wider shrink-0">DL</span>;
            return <span className="text-[8px] font-mono font-bold bg-yellow-400/10 text-yellow-400 border border-yellow-400/30 px-1.5 py-0.5 rounded-sm uppercase tracking-wider shrink-0">OL</span>;
          })()}
          <span className="font-mono text-[10px] text-muted-foreground shrink-0">{formatDuration(elapsedSeconds)}</span>
        </div>
        {/* Right: voice + end session */}
        <div className="flex items-center gap-2 shrink-0">
          <VoiceMicButton
            mode="session"
            onResult={handleVoiceResult}
            onError={(msg) => toast.error(`🎤 ${msg}`, { duration: 2500 })}
            context={activeExercise?.name}
            size="sm"
          />
          <button
            onClick={handleFinishWorkout}
            className="font-mono text-[10px] text-muted-foreground hover:text-destructive transition-colors uppercase tracking-wider"
          >
            END SESSION
          </button>
        </div>
      </div>

      {/* ── STICKY EXERCISE HEADER ───────────────────────────────────────────── */}
      {activePlannedExercise && (
        <div className={`sticky top-0 z-20 backdrop-blur-md border-b border-white/10 px-4 py-2.5 transition-all duration-200 ${
          supersetHighlight ? 'border-orange-500/30' : ''
        }`} style={{ background: supersetHighlight ? 'rgba(249,115,22,0.12)' : 'rgba(26,26,26,0.97)', willChange: 'transform', transform: 'translateZ(0)' }}>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="icon"
              disabled={activeExerciseIndex === 0}
              onClick={() => setActiveExerciseIndex(prev => prev - 1)}
              className="h-7 w-7 text-muted-foreground hover:text-primary shrink-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1 min-w-0 text-center">
              <h2 className="font-bold text-lg text-white truncate leading-tight">{activeExercise?.name}</h2>
              <div className="flex items-center justify-center gap-2 mt-0.5">
                <span className="text-[10px] font-mono text-white/50 uppercase">
                  {activeExerciseIndex + 1} / {session.plannedExercises.length} · {activeExercise?.category}
                </span>
                {activePlannedExercise.supersetWithId && (() => {
                  const paired = session.plannedExercises.find(p => p.id === activePlannedExercise.supersetWithId);
                  const pairedEx = paired ? exercises.find(e => e.id === paired.exerciseId) : null;
                  return pairedEx ? (
                    <span className="inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', color: 'rgb(251,146,60)' }}>
                      <Link className="h-2.5 w-2.5" />
                      SS → {pairedEx.name.toUpperCase()}
                    </span>
                  ) : null;
                })()}
              </div>
            </div>
            <Button
              variant="ghost" size="icon"
              disabled={activeExerciseIndex === session.plannedExercises.length - 1}
              onClick={() => setActiveExerciseIndex(prev => prev + 1)}
              className="h-7 w-7 text-muted-foreground hover:text-primary shrink-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── SCROLLABLE CONTENT ──────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto pb-32" style={{ WebkitOverflowScrolling: 'touch' }}>

        {activePlannedExercise ? (
          <div className="px-4 pt-3 space-y-4">

            {/* ── ACTIVE EXERCISE BLOCK ───────────────────────────────────── */}
            <div className="space-y-3">

              {/* Previous session history */}
              {previousHistory && (
                <div className="border border-primary/20 rounded-sm px-3 py-2 text-xs font-mono" style={{ background: 'rgba(var(--primary-rgb,99,102,241),0.08)' }}>
                  <div className="text-[9px] text-muted-foreground uppercase mb-1">LAST TIME ({previousHistory.date})</div>
                  <div className="flex flex-wrap gap-1.5 text-primary font-bold">
                    {previousHistory.sets.map((set, idx) => {
                      const rpeLabel = set.rpe !== undefined ? ` RPE${set.rpe}` : set.rir !== undefined ? ` RIR${set.rir}` : '';
                      return (
                        <span key={idx} className={`px-1.5 py-0.5 rounded border text-[10px] flex items-center gap-1 ${
                          set.setType === 'kill' ? 'bg-destructive/10 border-destructive/40 text-destructive'
                          : set.setType === 'drop_set' ? 'bg-amber-500/10 border-amber-500/40 text-amber-600'
                          : set.isFailure ? 'bg-orange-500/10 border-orange-500/40 text-orange-500'
                          : set.isAmrap ? 'bg-blue-500/10 border-blue-500/40 text-blue-500'
                          : 'bg-secondary border-border'
                        }`}>
                          {set.setType === 'kill' && <Skull className="h-2.5 w-2.5" />}
                          {set.setType === 'drop_set' && <TrendingDown className="h-2.5 w-2.5" />}
                          {set.isFailure && <AlertTriangle className="h-2.5 w-2.5" />}
                          {set.isAmrap && <Target className="h-2.5 w-2.5" />}
                          {set.setType === 'drop_set' && set.dropSetEntries && set.dropSetEntries.length > 0
                            ? set.dropSetEntries.map((d) => `${d.weight}×${d.reps}`).join(' → ')
                            : activeExercise?.trackingMode === 'time'
                            ? `${set.durationSeconds}s${set.weight ? ` × ${set.weight}lbs` : ''}`
                            : `${set.weight}lbs × ${set.reps}${set.isAmrap ? ' AMRAP' : ''}${set.isFailure ? ' FAIL' : ''}${rpeLabel}`}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── SET ROWS ─────────────────────────────────────────────── */}
              {/* Determine which set is currently active (first uncompleted non-child) */}
              {(() => {
                const activeSetId = activePlannedExercise.sets.find(s => !s.completed && !s.isChildBlock)?.id ?? null;

                // Pre-process: build a list of "render groups" so drop set parents + children can be collapsed together
                type RenderGroup = { parentIdx: number; childIdxs: number[]; isDropGroup: boolean };
                const renderGroups: RenderGroup[] = [];
                const skipIdxs = new Set<number>();
                activePlannedExercise.sets.forEach((s, i) => {
                  if (skipIdxs.has(i)) return;
                  const isDropParent = (s.setType === 'drop' || s.setType === 'drop_set') && !s.isChildBlock;
                  if (isDropParent) {
                    const childIdxs: number[] = [];
                    for (let j = i + 1; j < activePlannedExercise.sets.length; j++) {
                      const child = activePlannedExercise.sets[j];
                      if (child?.isChildBlock) { childIdxs.push(j); skipIdxs.add(j); }
                      else break;
                    }
                    renderGroups.push({ parentIdx: i, childIdxs, isDropGroup: true });
                  } else {
                    renderGroups.push({ parentIdx: i, childIdxs: [], isDropGroup: false });
                  }
                });

                return (
              <div className="space-y-1.5">
                {renderGroups.map(({ parentIdx, childIdxs, isDropGroup }) => {
                  const set = activePlannedExercise.sets[parentIdx]!;
                  const idx = parentIdx;
                  const isActiveSet = set.id === activeSetId && !set.isChildBlock;
                  const isChildBlock = !!set.isChildBlock;
                  const isFeeder = set.setType === 'feeder';
                  const isBackOff = set.setType === 'back_off';
                  const isWarmupSet = set.setType === 'warmup' || !!set.isWarmup;
                  const isKillSet = set.setType === 'kill';
                  const isDropLegacy = set.setType === 'drop_set';
                  const isDropNew = set.setType === 'drop' && !isChildBlock;
                  const isCluster = set.setType === 'cluster' && !isChildBlock;
                  const isMech = set.setType === 'mechanical' && !isChildBlock;
                  const isAmrapSet = set.setType === 'amrap' || !!set.isAmrap;
                  const isRestPause = set.setType === 'rest_pause';
                  const isOverloadSet = set.setType === 'overload_set';
                  const isHyperSet = set.setType === 'hyper_set';
                  const isFailureSet = set.setType === 'failure';

                  const workingSetNum = (() => {
                    let n = 0;
                    for (let i = 0; i <= idx; i++) {
                      const s = activePlannedExercise.sets[i];
                      if (!s) continue;
                      if (s.isChildBlock || s.setType === 'warmup' || s.isWarmup ||
                          s.setType === 'feeder' || s.setType === 'kill' ||
                          s.setType === 'drop_set' || s.setType === 'failure' ||
                          s.setType === 'amrap' || s.setType === 'back_off') continue;
                      n++;
                    }
                    return n;
                  })();

                  const rowStyle = completingSetId === set.id
                    ? 'scale-[1.02] border-success shadow-[0_0_12px_rgba(34,197,94,0.35)]'
                    : set.completed
                    ? 'border-success/30 bg-[#1e2e1e]'
                    : isWarmupSet ? 'border-l-[3px] border-l-success border-success/25 bg-success/5'
                    : isKillSet ? 'border-l-[3px] border-l-destructive border-destructive/25 bg-destructive/5'
                    : isDropLegacy || isDropNew || (isChildBlock && set.setType === 'drop') ? 'border-l-[3px] border-l-amber-500 border-amber-500/25 bg-amber-500/5'
                    : isFailureSet ? 'border-l-[3px] border-l-orange-500 border-orange-500/25 bg-orange-500/5'
                    : isAmrapSet ? 'border-l-[3px] border-l-blue-500 border-blue-500/25 bg-blue-500/5'
                    : isRestPause ? 'border-l-[3px] border-l-violet-500 border-violet-500/25 bg-violet-500/5'
                    : isCluster || (isChildBlock && set.setType === 'cluster') ? 'border-l-[3px] border-l-cyan-500 border-cyan-500/25 bg-cyan-500/5'
                    : isMech || (isChildBlock && set.setType === 'mechanical') ? 'border-l-[3px] border-l-purple-500 border-purple-500/25 bg-purple-500/5'
                    : isFeeder ? 'border-l-[3px] border-l-slate-400 border-slate-400/25 bg-slate-400/5'
                    : isBackOff ? 'border-l-[3px] border-l-teal-500 border-teal-500/25 bg-teal-500/5'
                    : isOverloadSet ? 'border-l-[3px] border-l-red-500 border-red-500/25 bg-red-500/5'
                    : isHyperSet ? 'border-l-[3px] border-l-emerald-500 border-emerald-500/25 bg-emerald-500/5'
                    : 'bg-[#2a2a2a] border-[#3a3a3a]';

                  const typeAbbrev: Record<string, string> = {
                    normal: 'ST', straight: 'ST',
                    overload: 'OL', overload_set: 'OL', kill: 'OL',
                    hypertrophy: 'HY', hyper_set: 'HY',
                    drop: 'DR', drop_set: 'DR',
                    superset: 'SU', giant: 'GI',
                    rest_pause: 'RP',
                    mechanical: 'ME', mechanical_drop: 'ME',
                    cluster: 'CL',
                    amrap: 'AM', failure: 'AM',
                    feeder: 'FE', warmup: 'FE',
                    back_off: 'BO',
                  };
                  const abbrev = typeAbbrev[set.setType ?? 'normal'] ?? 'ST';
                  const typeCfg = getSetTypeBadgeConfig(set.setType as any, set.isWarmup);

                  const setLabel = isChildBlock
                    ? (set.childLabel ?? '?')
                    : isWarmupSet ? 'W'
                    : isKillSet ? 'K'
                    : isFeeder ? (set.childLabel ?? 'F')
                    : isBackOff ? 'B'
                    : `SET ${workingSetNum}`;

                  // Focus layout: determine set role
                  const isEditingSet = editingSetId === set.id;
                  const isUpcomingSet = !set.completed && !isActiveSet && !isChildBlock;
                  // Compact mode: completed (not editing) or upcoming non-child
                  const isCompact = (set.completed && !isEditingSet) || isUpcomingSet;

                  // Drop group collapse: if this is a drop parent and ALL children are completed, render as single summary row
                  const isDropGroupExpanded = editingSetId === set.id; // tap to expand
                  if (isDropGroup && set.completed && childIdxs.length > 0 && !isDropGroupExpanded) {
                    const childSets = childIdxs.map(ci => activePlannedExercise.sets[ci]!);
                    const allChildrenDone = childSets.every(c => c.completed);
                    if (allChildrenDone) {
                      // Build summary: parent + children chain
                      const parentSummary = set.weight !== undefined && set.reps !== undefined
                        ? `${set.weight}lbs×${set.reps}`
                        : set.weight !== undefined ? `${set.weight}lbs×?` : set.reps !== undefined ? `?×${set.reps}` : '—';
                      const childSummaries = childSets.map(c =>
                        c.weight !== undefined && c.reps !== undefined ? `${c.weight}lbs×${c.reps}`
                        : c.weight !== undefined ? `${c.weight}lbs×?` : c.reps !== undefined ? `?×${c.reps}` : '—'
                      );
                      const fullChain = [parentSummary, ...childSummaries].join(' → ');
                      const dropSwipeOffset = swipeState[set.id] ?? 0;
                      const dropIsSwipedOpen = dropSwipeOffset < -60;
                      return (
                        <div key={set.id} className="relative overflow-hidden">
                          <div className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-20 bg-destructive/90 z-10 rounded-r-sm"
                            style={{ opacity: Math.min(1, Math.abs(dropSwipeOffset) / 60) }}>
                            <button onClick={() => {
                              handleRemoveSet(activePlannedExercise.id, set.id);
                              childIdxs.forEach(ci => {
                                const cs = activePlannedExercise.sets[ci];
                                if (cs) handleRemoveSet(activePlannedExercise.id, cs.id);
                              });
                              setSwipeState(prev => { const n = { ...prev }; delete n[set.id]; return n; });
                            }} className="flex flex-col items-center gap-1 text-white">
                              <Trash2 className="h-4 w-4" />
                              <span className="text-[8px] font-mono uppercase">DELETE</span>
                            </button>
                          </div>
                          <div
                            style={{ transform: `translateX(${dropSwipeOffset}px)`, transition: 'transform 0.15s ease-out' }}
                            onTouchStart={(e) => { swipeTouchStartRef.current[set.id] = e.touches[0].clientX; }}
                            onTouchMove={(e) => { const sx = swipeTouchStartRef.current[set.id] ?? e.touches[0].clientX; setSwipeState(prev => ({ ...prev, [set.id]: Math.min(0, Math.max(-90, e.touches[0].clientX - sx)) })); }}
                            onTouchEnd={() => { const o = swipeState[set.id] ?? 0; setSwipeState(prev => ({ ...prev, [set.id]: o < -50 ? -80 : 0 })); }}
                            onClick={dropIsSwipedOpen ? () => setSwipeState(prev => ({ ...prev, [set.id]: 0 })) : undefined}
                          >
                            <div className={`rounded-sm border px-2 pt-2 pb-1.5 opacity-60 bg-success/5 border-success/30`}>
                              <button className="w-full flex items-center gap-2 text-left"
                                onClick={() => !dropIsSwipedOpen && setEditingSetId(set.id)}>
                                <span className="font-mono text-[10px] font-bold text-muted-foreground shrink-0 w-10">{setLabel}</span>
                                <span className={`px-1 py-0.5 rounded-sm border font-mono text-[8px] font-bold leading-none shrink-0 ${
                                  typeCfg ? `${typeCfg.colorClass} ${typeCfg.bgClass} ${typeCfg.borderClass}` : 'text-amber-400 border-amber-500/30 bg-amber-500/10'
                                }`}>DR</span>
                                <div className="flex-1 font-mono text-[10px] text-muted-foreground truncate">{fullChain}</div>
                                <span className="text-[8px] font-mono text-success/70 shrink-0">TAP TO EDIT</span>
                                <Check className="h-3 w-3 text-success shrink-0" />
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    }
                  }

                  // Swipe-to-delete helpers — available on uncompleted sets AND completed sets (for removal)
                  const swipeOffset = swipeState[set.id] ?? 0;
                  const isSwipedOpen = swipeOffset < -60;
                  const handleTouchStart = (e: React.TouchEvent) => {
                    swipeTouchStartRef.current[set.id] = e.touches[0].clientX;
                  };
                  const handleTouchMove = (e: React.TouchEvent) => {
                    const startX = swipeTouchStartRef.current[set.id] ?? e.touches[0].clientX;
                    const dx = Math.min(0, Math.max(-90, e.touches[0].clientX - startX));
                    setSwipeState(prev => ({ ...prev, [set.id]: dx }));
                  };
                  const handleTouchEnd = () => {
                    const offset = swipeState[set.id] ?? 0;
                    setSwipeState(prev => ({ ...prev, [set.id]: offset < -50 ? -80 : 0 }));
                  };

                  return (
                    <div key={set.id} className={`relative overflow-hidden ${isChildBlock ? 'ml-4 border-l-2 border-dashed border-border/40 pl-2' : ''}`}>
                      {/* Swipe-reveal delete button (available on all sets) */}
                      <div
                        className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-20 bg-destructive/90 z-10 rounded-r-sm"
                        style={{ opacity: Math.min(1, Math.abs(swipeOffset) / 60) }}
                      >
                        <button
                          onClick={() => {
                            handleRemoveSet(activePlannedExercise.id, set.id);
                            setSwipeState(prev => { const n = { ...prev }; delete n[set.id]; return n; });
                            if (editingSetId === set.id) setEditingSetId(null);
                          }}
                          className="flex flex-col items-center gap-1 text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="text-[8px] font-mono uppercase">DELETE</span>
                        </button>
                      </div>
                      <div
                        style={{ transform: `translateX(${swipeOffset}px)`, transition: 'transform 0.15s ease-out' }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        onClick={isSwipedOpen ? () => setSwipeState(prev => ({ ...prev, [set.id]: 0 })) : undefined}
                      >
                      <div className={`rounded-sm border px-2 pt-2 pb-1.5 transition-all duration-300 ease-out ${isChildBlock ? 'border-transparent' : ''} ${rowStyle} ${
                        isCompact ? 'opacity-60' : ''
                      } ${
                        isActiveSet ? 'ring-1 ring-primary/30 shadow-sm' : ''
                      }`}>

                        {/* ── COMPACT ROW (completed or upcoming) ── */}
                        {isCompact && !isChildBlock ? (
                          <button
                            className="w-full flex items-center gap-2 text-left"
                            onClick={() => {
                              if (!isSwipedOpen) {
                                setEditingSetId(set.id);
                              }
                            }}
                          >
                            <span className="font-mono text-[10px] font-bold text-muted-foreground shrink-0 w-10">{setLabel}</span>
                            <span className={`px-1.5 py-0.5 rounded-sm border font-mono text-[9px] font-bold leading-none shrink-0 ${
                              typeCfg ? `${typeCfg.colorClass} ${typeCfg.bgClass} ${typeCfg.borderClass}` : 'text-muted-foreground border-border/30'
                            }`}>{abbrev}</span>
                            <div className="flex-1 font-mono text-[10px] text-muted-foreground">
                              {activeExercise?.trackingMode === 'time'
                                ? set.durationSeconds ? `${set.durationSeconds}s${set.weight ? ` × ${set.weight}lbs` : ''}` : '—'
                                : set.weight !== undefined && set.reps !== undefined
                                  ? `${set.weight}lbs × ${set.reps}${set.isAmrap ? ' AMRAP' : ''}${set.rpe !== undefined ? ` RPE${set.rpe}` : set.rir !== undefined ? ` RIR${set.rir}` : ''}`
                                  : set.reps !== undefined ? `? × ${set.reps}` : set.weight !== undefined ? `${set.weight}lbs × ?` : '—'
                              }
                            </div>
                            <span className={`text-[8px] font-mono shrink-0 ${set.completed ? 'text-success/70' : 'text-muted-foreground/50'}`}>TAP TO EDIT</span>
                            {set.isPR && <Award className="h-3 w-3 text-primary animate-pulse shrink-0" />}
                            {set.completed && <Check className="h-3 w-3 text-success shrink-0" />}
                          </button>
                        ) : (
                        <>
                        {/* ── FULL ROW (active set or editing completed set) ── */}
                        {isEditingSet && (
                          <div className="flex items-center gap-1.5 px-2 pt-1.5 pb-0.5">
                            <span className="text-[8px] font-mono text-amber-400 uppercase tracking-wider">EDITING — SAVE TO UPDATE</span>
                            <button onClick={() => setEditingSetId(null)} className="ml-auto text-[8px] font-mono text-muted-foreground hover:text-foreground uppercase">CANCEL</button>
                          </div>
                        )}
                        {/* LINE 1: label · type badge · [revert] · trash */}
                        <div className="flex items-center gap-2 mb-1.5 -mt-1">
                          <span className={`font-mono text-[11px] font-bold shrink-0 ${
                            isChildBlock
                              ? set.setType === 'drop' ? 'text-amber-400'
                                : set.setType === 'cluster' ? 'text-cyan-400'
                                : set.setType === 'mechanical' ? 'text-purple-400'
                                : 'text-muted-foreground'
                              : 'text-foreground'
                          }`}>{setLabel}</span>
                          {set.isPR && <Award className="h-3 w-3 text-primary animate-pulse shrink-0" />}
                          {!isChildBlock ? (
                            <button
                              onClick={() => (isEditingSet || !set.completed) && setSetTypePicker(
                                setTypePicker?.peId === activePlannedExercise.id && setTypePicker?.setId === set.id
                                  ? null
                                  : { peId: activePlannedExercise.id, setId: set.id }
                              )}
                              disabled={set.completed && !isEditingSet}
                              title="Set type — tap to change"
                              className={`px-1.5 py-0.5 rounded-sm border font-mono text-[9px] font-bold leading-none transition-all shrink-0 ${
                                (set.completed && !isEditingSet) ? 'opacity-40 cursor-default' : 'hover:opacity-100 active:scale-95'
                              } ${
                                typeCfg ? `${typeCfg.colorClass} ${typeCfg.bgClass} ${typeCfg.borderClass}` : 'text-slate-400 border-slate-500/30 bg-slate-500/10'
                              }`}
                            >
                              {abbrev}
                            </button>
                          ) : (
                            <span className={`px-1.5 py-0.5 rounded-sm border font-mono text-[9px] font-bold leading-none shrink-0 ${
                              typeCfg ? `${typeCfg.colorClass} ${typeCfg.bgClass} ${typeCfg.borderClass}` : 'text-muted-foreground border-border/30 bg-transparent'
                            }`}>{abbrev}</span>
                          )}
                          <div className="flex-1" />
                          {!set.completed && !isChildBlock && ['drop', 'cluster', 'mechanical'].includes(set.setType ?? '') && (
                            <button
                              onClick={() => {
                                handleRevertSetToStraight(activePlannedExercise.id, set.id);
                                toast.success('Reverted to straight set');
                              }}
                              title="Revert to Straight set"
                              className="text-muted-foreground hover:text-amber-500 transition-colors p-1"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {!set.completed && activePlannedExercise.sets.length > 1 && (
                            <button
                              onClick={() => handleRemoveSet(activePlannedExercise.id, set.id)}
                              className="text-muted-foreground hover:text-destructive transition-colors p-1"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>

                        {/* LINE 2: inputs */}
                        <div className="flex items-center gap-2">
                          {activeExercise?.trackingMode === 'time' ? (
                            <>
                              <Input
                                type="number" placeholder="Secs" disabled={set.completed && !isEditingSet}
                                value={set.durationSeconds ?? ''}
                                onChange={(e) => handleSetChange(activePlannedExercise.id, set.id, 'durationSeconds', e.target.value ? parseInt(e.target.value) : undefined)}
                                className="h-10 flex-1 text-center font-mono text-base text-white bg-[#2e2e2e] border-[#444] placeholder:text-white/30"
                                inputMode="numeric"
                              />
                              <span className="text-white/40 font-mono text-[10px] shrink-0">×</span>
                              <Input
                                type="number" placeholder="LBS" disabled={set.completed && !isEditingSet}
                                value={set.weight ?? ''}
                                onChange={(e) => handleSetChange(activePlannedExercise.id, set.id, 'weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                                className="h-10 flex-1 text-center font-mono text-base text-white bg-[#2e2e2e] border-[#444] placeholder:text-white/30"
                                inputMode="decimal"
                              />
                              <span className="text-white/40 font-mono text-[10px] shrink-0">LBS</span>
                              <PlateSelector currentWeight={set.weight} onChange={(val) => handleSetChange(activePlannedExercise.id, set.id, 'weight', val)} disabled={set.completed && !isEditingSet} size="xs" />
                            </>
                          ) : (
                            <>
                              <Input
                                type="number"
                                placeholder={isAmrapSet ? '∞' : 'REPS'}
                                disabled={set.completed && !isEditingSet}
                                value={set.reps ?? ''}
                                onChange={(e) => handleSetChange(activePlannedExercise.id, set.id, 'reps', e.target.value ? parseInt(e.target.value) : undefined)}
                                className={`h-10 flex-1 text-center font-mono text-base text-white bg-[#2e2e2e] border-[#444] placeholder:text-white/30 transition-colors duration-300 ${
                                  voiceFlashSetId === set.id ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_8px_rgba(34,197,94,0.4)]' :
                                  isAmrapSet ? 'border-blue-500/50 bg-blue-500/5' : ''
                                }`}
                                inputMode="numeric"
                              />
                              <span className="text-white/40 font-mono text-sm shrink-0">×</span>
                              <Input
                                type="number" placeholder="LBS" disabled={set.completed && !isEditingSet}
                                value={set.weight ?? ''}
                                onChange={(e) => handleSetChange(activePlannedExercise.id, set.id, 'weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                                className={`h-10 flex-1 text-center font-mono text-base text-white bg-[#2e2e2e] border-[#444] placeholder:text-white/30 transition-colors duration-300 ${
                                  voiceFlashSetId === set.id ? 'border-emerald-500 bg-emerald-500/10 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : ''
                                }`}
                                inputMode="decimal"
                              />
                              <span className="text-white/40 font-mono text-[10px] shrink-0">LBS</span>
                              <PlateSelector currentWeight={set.weight} onChange={(val) => handleSetChange(activePlannedExercise.id, set.id, 'weight', val)} disabled={set.completed && !isEditingSet} size="xs" />
                            </>
                          )}
                        </div>

                        {/* Sub-notes */}
                        {set.setType === 'drop_set' && (
                          <div className="ml-7 mt-1 space-y-1 border-l-2 border-amber-500/30 pl-2">
                            <div className="text-[9px] font-mono text-amber-600 uppercase tracking-wider mb-1">DROP ENTRIES</div>
                            {(set.dropSetEntries ?? [{ weight: undefined, reps: undefined }]).map((entry, di) => (
                              <div key={di} className="flex items-center gap-1.5">
                                <span className="text-[9px] font-mono text-amber-500/70 w-5 shrink-0">↓{di + 1}</span>
                                <Input type="number" placeholder="LBS" disabled={set.completed} value={(entry as any).weight ?? ''}
                                  onChange={(e) => {
                                    const entries = [...(set.dropSetEntries ?? [{ weight: undefined, reps: undefined }])];
                                    entries[di] = { ...entries[di], weight: e.target.value ? parseFloat(e.target.value) : undefined };
                                    handleSetChange(activePlannedExercise.id, set.id, 'dropSetEntries', entries);
                                  }}
                                  className="h-7 w-20 text-center font-mono text-xs bg-amber-500/5 border-amber-500/30 text-foreground"
                                />
                                <span className="text-[9px] font-mono text-muted-foreground">×</span>
                                <Input type="number" placeholder="REPS" disabled={set.completed} value={(entry as any).reps ?? ''}
                                  onChange={(e) => {
                                    const entries = [...(set.dropSetEntries ?? [{ weight: undefined, reps: undefined }])];
                                    entries[di] = { ...entries[di], reps: e.target.value ? parseInt(e.target.value) : undefined };
                                    handleSetChange(activePlannedExercise.id, set.id, 'dropSetEntries', entries);
                                  }}
                                  className="h-7 w-16 text-center font-mono text-xs bg-amber-500/5 border-amber-500/30 text-foreground"
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {set.setType === 'cluster' && set.microRestSeconds && (
                          <div className="ml-8 mt-0.5"><span className="text-[9px] font-mono text-cyan-500/70 uppercase">⏱ {set.microRestSeconds}s micro-rest between clusters</span></div>
                        )}
                        {set.setType === 'mechanical' && set.mechVariation && (
                          <div className="ml-8 mt-0.5"><span className="text-[9px] font-mono text-purple-400/70 uppercase">↻ {set.mechVariation}</span></div>
                        )}
                        {isRestPause && (
                          <div className="ml-8 mt-0.5"><span className="text-[9px] font-mono text-violet-400/70 uppercase">Log total combined reps across all mini-sets</span></div>
                        )}
                        {(set.setType === 'normal' || set.setType === 'failure' || set.setType === 'amrap' || !set.setType) && (
                          <div className="ml-7 mt-1 flex items-center gap-2">
                            <span className="text-[9px] font-mono text-muted-foreground/60 uppercase">RPE</span>
                            <Input type="number" placeholder="1-10" min={1} max={10}
                              disabled={set.completed && !isEditingSet}
                              value={set.rpe ?? ''}
                              onChange={(e) => handleSetChange(activePlannedExercise.id, set.id, 'rpe', e.target.value ? parseFloat(e.target.value) : undefined)}
                              className="h-6 w-14 text-center font-mono text-[10px] bg-secondary/50 border-border/40 text-foreground"
                            />
                            <span className="text-[9px] font-mono text-muted-foreground/60 uppercase">RIR</span>
                            <Input type="number" placeholder="0-5" min={0} max={5}
                              disabled={set.completed && !isEditingSet}
                              value={set.rir ?? ''}
                              onChange={(e) => handleSetChange(activePlannedExercise.id, set.id, 'rir', e.target.value ? parseInt(e.target.value) : undefined)}
                              className="h-6 w-14 text-center font-mono text-[10px] bg-secondary/50 border-border/40 text-foreground"
                            />
                          </div>
                        )}
                        {/* SAVE button when editing a completed set */}
                        {isEditingSet && (
                          <div className="flex justify-end px-1 pb-1 pt-1.5">
                            <button
                              onClick={() => setEditingSetId(null)}
                              className="font-mono text-[10px] px-3 py-1 bg-success/20 border border-success/50 text-success rounded-sm hover:bg-success/30 active:scale-95 transition-all uppercase tracking-wider"
                            >
                              <Check className="h-3 w-3 inline mr-1" />SAVE
                            </button>
                          </div>
                        )}
                      </>
                        )}
                      </div>
                      </div>
                    </div>
                  );
                })}

              </div>
                );
              })()}

              {/* Add set palette */}
              <div className="mt-2 space-y-1.5">
                <div className="text-[9px] font-mono text-white/50 uppercase tracking-widest">ADD SET</div>
                <div className="grid grid-cols-4 gap-1.5">
                  <button onClick={() => handleAddSet(activePlannedExercise.id, 'normal')} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-border hover:border-primary/60 hover:bg-primary/5 rounded-sm transition-all active:scale-95">
                    <Dumbbell className="h-4 w-4 text-white/60" />
                    <span className="text-[9px] font-mono text-white/60 uppercase">WORK</span>
                  </button>
                  <button onClick={() => handleAddSet(activePlannedExercise.id, 'warmup')} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-success/40 hover:border-success/70 hover:bg-success/5 rounded-sm transition-all active:scale-95">
                    <Flame className="h-4 w-4 text-success" />
                    <span className="text-[9px] font-mono text-success uppercase">WARM</span>
                  </button>
                  <button onClick={() => handleAddSet(activePlannedExercise.id, 'kill')} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-destructive/40 hover:border-destructive/70 hover:bg-destructive/5 rounded-sm transition-all active:scale-95">
                    <Skull className="h-4 w-4 text-destructive" />
                    <span className="text-[9px] font-mono text-destructive uppercase">KILL</span>
                  </button>
                  <button onClick={() => handleAutoCompleteAllSets(activePlannedExercise.id)} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-primary/40 hover:border-primary/70 hover:bg-primary/5 rounded-sm transition-all active:scale-95">
                    <Zap className="h-4 w-4 text-primary" />
                    <span className="text-[9px] font-mono text-primary uppercase">ALL ✓</span>
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <button onClick={() => handleAddSet(activePlannedExercise.id, 'amrap')} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-blue-500/40 hover:border-blue-500/70 hover:bg-blue-500/5 rounded-sm transition-all active:scale-95">
                    <Target className="h-4 w-4 text-blue-500" />
                    <span className="text-[9px] font-mono text-blue-400 uppercase">AMRAP</span>
                  </button>
                  <button onClick={() => handleAddSet(activePlannedExercise.id, 'failure')} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-orange-500/40 hover:border-orange-500/70 hover:bg-orange-500/5 rounded-sm transition-all active:scale-95">
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                    <span className="text-[9px] font-mono text-orange-400 uppercase">FAIL</span>
                  </button>
                  <button onClick={() => handleAddSet(activePlannedExercise.id, 'drop_set')} className="flex flex-col items-center gap-1 py-2 px-1 border border-dashed border-amber-500/40 hover:border-amber-500/70 hover:bg-amber-500/5 rounded-sm transition-all active:scale-95">
                    <TrendingDown className="h-4 w-4 text-amber-500" />
                    <span className="text-[9px] font-mono text-amber-400 uppercase">DROP</span>
                  </button>
                </div>
              </div>

              {/* Core timer station (time-based exercises only) */}
              {activeExercise?.trackingMode === 'time' && (
                <div className="mt-2 p-3 border border-border/60 bg-secondary/20 rounded-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs font-bold text-primary flex items-center gap-1.5">
                      <Timer className="h-3.5 w-3.5" /> CORE TIMER
                    </span>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant={coreTimerMode === 'countdown' ? 'default' : 'outline'}
                        onClick={() => { setCoreTimerActive(false); setCoreTimerMode('countdown'); setCoreTimerSeconds(coreTimerPreset); }}
                        className="h-6 font-mono text-[9px] px-2">COUNTDOWN</Button>
                      <Button size="sm" variant={coreTimerMode === 'stopwatch' ? 'default' : 'outline'}
                        onClick={() => { setCoreTimerActive(false); setCoreTimerMode('stopwatch'); setCoreTimerSeconds(0); }}
                        className="h-6 font-mono text-[9px] px-2">STOPWATCH</Button>
                    </div>
                  </div>
                  <div className="flex flex-col items-center py-2">
                    <div className="relative w-28 h-28 flex items-center justify-center mb-3">
                      <svg className="w-full h-full transform -rotate-90 absolute">
                        <circle cx="56" cy="56" r="44" className="stroke-muted-foreground/10 fill-none" strokeWidth="3" />
                        <circle cx="56" cy="56" r="44"
                          className={`fill-none transition-all duration-300 ${
                            coreTimerActive && coreTimerMode === 'countdown' && coreTimerSeconds <= 5 ? 'stroke-destructive' : 'stroke-primary'
                          }`}
                          strokeWidth="3"
                          strokeDasharray="276.5"
                          strokeDashoffset={coreTimerMode === 'countdown' && coreTimerPreset > 0 ? 276.5 * (1 - coreTimerSeconds / coreTimerPreset) : 0}
                        />
                      </svg>
                      <div className="z-10 flex flex-col items-center">
                        <span className="font-mono text-3xl font-bold text-primary">
                          {coreTimerMode === 'countdown'
                            ? `${Math.floor(coreTimerSeconds / 60)}:${(coreTimerSeconds % 60).toString().padStart(2, '0')}`
                            : `${Math.floor(coreTimerSeconds / 60)}:${(coreTimerSeconds % 60).toString().padStart(2, '0')}`}
                        </span>
                        <span className="text-[9px] font-mono text-muted-foreground uppercase">{coreTimerActive ? (coreTimerPhase === 'rest' ? 'REST' : 'WORK') : 'READY'}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => { setCoreTimerActive(false); setCoreTimerSeconds(Math.max(0, coreTimerSeconds - 5)); }} className="h-8 font-mono text-[10px] px-2">-5S</Button>
                      <Button size="default" variant={coreTimerActive ? 'destructive' : 'default'}
                        onClick={() => { if (coreTimerActive) { setCoreTimerActive(false); if (coreTimerMode === 'countdown') setCoreTimerSeconds(coreTimerPreset); else setCoreTimerSeconds(0); } else setCoreTimerActive(true); }}
                        className="h-9 px-5 font-mono text-xs">{coreTimerActive ? 'STOP' : 'START'}</Button>
                      <Button size="sm" variant="outline" onClick={() => { setCoreTimerSeconds(prev => prev + 5); }} className="h-8 font-mono text-[10px] px-2">+5S</Button>
                    </div>
                    {coreActiveSetId && (
                      <Button size="sm" onClick={() => {
                        triggerCoreTimerEnd(); setCoreTimerActive(false);
                        handleSetChange(activePlannedExercise.id, coreActiveSetId, 'durationSeconds', coreTimerSeconds || coreTimerPreset);
                        handleToggleSet(activePlannedExercise.id, coreActiveSetId);
                        const currentIdx = activePlannedExercise.sets.findIndex(s => s.id === coreActiveSetId);
                        const nextSet = activePlannedExercise.sets.slice(currentIdx + 1).find(s => !s.completed);
                        if (nextSet) { setCoreActiveSetId(nextSet.id); if (coreTimerMode === 'countdown') { setCoreTimerPreset(nextSet.durationSeconds || 60); setCoreTimerSeconds(nextSet.durationSeconds || 60); } else setCoreTimerSeconds(0); } else setCoreActiveSetId(null);
                      }} className="mt-2 h-7 px-4 bg-success text-success-foreground hover:bg-success/90 font-mono text-[10px]">
                        <Check className="h-3 w-3 mr-1" /> LOG TIME ({coreTimerSeconds || coreTimerPreset}s)
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ── COLLAPSED EXERCISE LIST ──────────────────────────────────── */}
            <div className="space-y-1 pt-2">
              <div className="text-[9px] font-mono text-muted-foreground uppercase tracking-widest px-0.5 mb-2">SESSION EXERCISES</div>
              {session.plannedExercises.map((pe, peIdx) => {
                if (peIdx === activeExerciseIndex) return null; // active exercise shown above
                const ex = exercises.find(e => e.id === pe.exerciseId);
                const totalSets = pe.sets.filter(s => !s.isChildBlock && s.setType !== 'feeder').length;
                const doneSets = pe.sets.filter(s => !s.isChildBlock && s.setType !== 'feeder' && s.completed).length;
                const allDone = doneSets === totalSets && totalSets > 0;
                const isExpanded = expandedExerciseId === pe.id;

                return (
                  <div key={pe.id} className={`border rounded-sm transition-all duration-200 ${
                    pe.skipped ? 'border-orange-500/20 bg-orange-500/5 opacity-60'
                    : allDone ? 'border-success/30 bg-success/5' : 'border-white/10 bg-[#2a2a2a]'
                  }`}>
                    {/* Header row */}
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => !pe.skipped && setExpandedExerciseId(isExpanded ? null : pe.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); !pe.skipped && setExpandedExerciseId(isExpanded ? null : pe.id); } }}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <span className={`font-mono text-xs font-bold truncate ${
                          pe.skipped ? 'text-orange-400 line-through'
                          : allDone ? 'text-success' : 'text-foreground'
                        }`}>{ex?.name ?? 'Unknown'}</span>
                        {pe.skipped && (
                          <div className="text-[9px] font-mono text-orange-400/70 mt-0.5">⚡ SKIPPED — {pe.skipReason ?? 'Too fatigued'}</div>
                        )}
                      </div>
                      {!pe.skipped && (
                        <span className={`font-mono text-[10px] shrink-0 ${
                          allDone ? 'text-success' : 'text-muted-foreground'
                        }`}>{doneSets}/{totalSets}</span>
                      )}
                      {allDone && !pe.skipped && <Check className="h-3.5 w-3.5 text-success shrink-0" />}
                      {pe.skipped ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleUnskipExercise(pe.id); }}
                          className="font-mono text-[9px] text-orange-400 hover:text-orange-300 transition-colors px-1.5 py-0.5 border border-orange-400/30 rounded-sm shrink-0"
                        >
                          RESTORE
                        </button>
                      ) : (
                        <>
                          {!allDone && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setSkipDialog({ peId: pe.id, exName: ex?.name ?? 'Exercise' }); }}
                              className="font-mono text-[9px] text-orange-400/70 hover:text-orange-400 transition-colors px-1.5 py-0.5 border border-orange-400/20 hover:border-orange-400/50 rounded-sm shrink-0"
                            >
                              SKIP
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setActiveExerciseIndex(peIdx); setExpandedExerciseId(null); }}
                            className="font-mono text-[9px] text-primary hover:text-primary/80 transition-colors px-1.5 py-0.5 border border-primary/30 rounded-sm shrink-0"
                          >
                            FOCUS
                          </button>
                        </>
                      )}
                      {!pe.skipped && (isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />)}
                    </div>

                    {/* Expanded set list */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-1.5 border-t border-border/30">
                        {pe.sets.filter(s => !s.isChildBlock && s.setType !== 'feeder').map((set, si) => (
                          <div key={set.id} className={`flex items-center gap-2 py-1.5 border-b border-border/20 last:border-0 ${
                            set.completed ? 'opacity-60' : ''
                          }`}>
                            <span className="font-mono text-[10px] text-muted-foreground w-12 shrink-0">SET {si + 1}</span>
                            <Input
                              type="number" placeholder="REPS" disabled={set.completed}
                              value={set.reps ?? ''}
                              onChange={(e) => handleSetChange(pe.id, set.id, 'reps', e.target.value ? parseInt(e.target.value) : undefined)}
                              className="h-7 w-16 text-center font-mono text-xs bg-secondary border-border text-foreground"
                              inputMode="numeric"
                            />
                            <span className="text-muted-foreground font-mono text-[10px]">×</span>
                            <Input
                              type="number" placeholder="LBS" disabled={set.completed}
                              value={set.weight ?? ''}
                              onChange={(e) => handleSetChange(pe.id, set.id, 'weight', e.target.value ? parseFloat(e.target.value) : undefined)}
                              className="h-7 w-20 text-center font-mono text-xs bg-secondary border-border text-foreground"
                              inputMode="decimal"
                            />
                            <div className="flex-1" />
                            <button
                              onClick={() => handleToggleSet(pe.id, set.id)}
                              className={`h-7 px-2 font-mono text-[9px] rounded-sm border transition-all ${
                                set.completed
                                  ? 'bg-success/10 border-success/40 text-success'
                                  : 'border-primary/40 text-primary hover:bg-primary/10'
                              }`}
                            >
                              {set.completed ? '✓' : 'LOG'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add exercise on the fly */}
              <Button
                variant="outline" size="sm"
                onClick={() => setShowLibrary(true)}
                className="w-full h-9 font-mono text-xs border-dashed border-primary/40 text-primary bg-primary/5 hover:bg-primary/10 flex items-center justify-center gap-1.5 mt-2"
              >
                <Plus className="h-4 w-4" /> ADD EXERCISE
              </Button>
            </div>

          </div>
        ) : (
          <div className="text-center py-12 font-mono text-sm text-muted-foreground px-4">
            NO MOVEMENTS IN THIS SESSION
          </div>
        )}
      </div>

      {/* ── HERO LOG SET BUTTON (fixed bottom) ──────────────────────────────── */}
      {activePlannedExercise && (() => {
        const nextSet = activePlannedExercise.sets.find(s => !s.completed);
        const allDone = !nextSet;
        const ringCircumference = 2 * Math.PI * 52; // r=52
        const ringProgress = timerActive && timerPreset > 0
          ? ringCircumference * (1 - timerSeconds / timerPreset)
          : allDone ? ringCircumference : 0;
        // Ring color: dark → amber at halfway → green when ready
        // timerSeconds/timerPreset > 0.5 means MORE than half the rest remains → still early (dark)
        // timerSeconds/timerPreset <= 0.5 means LESS than half remains → amber (halfway)
        // timerSeconds/timerPreset <= 0.2 means nearly done → green (ready)
        const ringColor = timerActive
          ? timerSeconds / timerPreset <= 0.2
            ? '#22c55e' // green — almost done, ready to go
            : timerSeconds / timerPreset <= 0.5
            ? '#f59e0b' // amber — halfway through rest
            : 'hsl(var(--primary))' // primary — early rest
          : allDone
          ? '#22c55e'
          : 'hsl(var(--primary))';
        const buttonBg = timerActive
          ? timerSeconds / timerPreset <= 0.2
            ? 'bg-emerald-500/10 border-emerald-500/40'
            : timerSeconds / timerPreset <= 0.5
            ? 'bg-amber-500/10 border-amber-500/40'
            : 'bg-primary/10 border-primary/40'
          : allDone
          ? 'bg-emerald-500/10 border-emerald-500/40'
          : 'bg-primary/10 border-primary/40';

        return (
          <div className="fixed bottom-0 left-0 right-0 z-30 backdrop-blur-sm border-t border-white/10 px-4 py-3" style={{ background: 'rgba(26,26,26,0.97)', transform: 'translateZ(0)' }}>
            {/* Session progress bar */}
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] text-muted-foreground">
                {completedSetsCount} of {totalSetsCount} sets complete
              </span>
              {timerActive && (
                <span className="font-mono text-[10px] text-amber-400">
                  REST: {Math.floor(timerSeconds / 60)}:{(timerSeconds % 60).toString().padStart(2, '0')}
                </span>
              )}
              <button
                onClick={() => setShowPlateDrawer(true)}
                className="font-mono text-[10px] text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
              >
                <Dumbbell className="h-3 w-3" /> PLATES
              </button>
            </div>

            {/* Hero LOG SET button with circular ring */}
            <div className="flex items-center justify-center">
              <button
                onClick={() => {
                  if (timerActive) {
                    // User is ready before timer — skip rest and log next set
                    setTimerActive(false);
                    setTimerSeconds(0);
                  }
                  if (nextSet) {
                    handleToggleSet(activePlannedExercise.id, nextSet.id);
                    // Auto-set rest timer duration based on set type
                    const autoRest = getAutoRestSeconds(nextSet);
                    if (autoRest > 0) setTimerPreset(autoRest);
                  } else if (allDone) {
                    // All sets done — advance to next exercise
                    if (activeExerciseIndex < session.plannedExercises.length - 1) {
                      setActiveExerciseIndex(prev => prev + 1);
                      toast.success('Exercise complete — next up!', { duration: 1500 });
                    } else {
                      handleFinishWorkout();
                    }
                  }
                }}
                className={`relative w-32 h-32 rounded-full border-2 flex items-center justify-center transition-[background-color,border-color,transform,opacity] duration-300 active:scale-95 ${
                  allDone ? 'bg-emerald-500/20 border-emerald-500/60' : buttonBg
                } ${timerActive && timerSeconds <= 5 ? 'animate-pulse' : ''}`}
              >
                {/* Circular progress ring */}
                <svg className="absolute inset-0 w-full h-full -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" strokeWidth="4" className="text-white/15" />
                  <circle
                    cx="60" cy="60" r="52" fill="none"
                    stroke={ringColor}
                    strokeWidth="4"
                    strokeDasharray={ringCircumference}
                    strokeDashoffset={ringCircumference - ringProgress}
                    strokeLinecap="round"
                    className="transition-all duration-300"
                  />
                </svg>
                {/* Button content */}
                <div className="z-10 flex flex-col items-center justify-center">
                  {timerActive ? (
                    <>
                      <span className="font-mono text-2xl font-bold" style={{ color: ringColor }}>
                        {Math.floor(timerSeconds / 60)}:{(timerSeconds % 60).toString().padStart(2, '0')}
                      </span>
                      <span className="font-mono text-[9px] text-muted-foreground uppercase mt-0.5">TAP TO LOG</span>
                    </>
                  ) : allDone ? (
                    <>
                      <Check className="h-8 w-8 text-emerald-500" />
                      <span className="font-mono text-[9px] text-emerald-400 uppercase mt-0.5">DONE</span>
                    </>
                  ) : (
                    <>
                      <span className="font-mono text-sm font-bold text-primary uppercase">LOG</span>
                      <span className="font-mono text-sm font-bold text-primary uppercase">SET</span>
                      {nextSet && (
                        <span className="font-mono text-[8px] text-muted-foreground mt-0.5">
                          {nextSet.weight ? `${nextSet.weight}×${nextSet.reps ?? '?'}` : 'SET READY'}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </button>
            </div>

            {/* Timer adjust row */}
            {!allDone && (
              <div className="flex items-center justify-center gap-3 mt-2">
                <button onClick={() => { setTimerPreset(p => Math.max(15, p - 15)); if (timerActive) setTimerSeconds(s => Math.max(0, s - 15)); }} className="font-mono text-[10px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 border border-border/40 rounded-sm">-15S</button>
                <button onClick={() => setShowRestDrawer(true)} className="font-mono text-[10px] text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
                  <Timer className="h-3 w-3" /> {timerPreset}S REST
                </button>
                <button onClick={() => { setTimerPreset(p => p + 15); if (timerActive) setTimerSeconds(s => s + 15); }} className="font-mono text-[10px] text-muted-foreground hover:text-foreground transition-colors px-2 py-1 border border-border/40 rounded-sm">+15S</button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── OVERLAYS ─────────────────────────────────────────────────────────── */}

      {/* Set type bottom-sheet picker */}
      {setTypePicker && activePlannedExercise && setTypePicker.peId === activePlannedExercise.id && (() => {
        const pickerSet = activePlannedExercise.sets.find(s => s.id === setTypePicker.setId);
        const currentType = pickerSet?.setType ?? 'normal';
        const currentDesc = ALL_SET_TYPES.find(t => t.value === currentType)?.info.description;
        return (
          <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex flex-col justify-end" onClick={(e) => { if (e.target === e.currentTarget) setSetTypePicker(null); }}>
            <div className="bg-card border-t border-border rounded-t-xl p-4 max-h-[70vh] flex flex-col shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-sm font-bold text-primary">SET TYPE</span>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSetTypePicker(null)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap gap-1.5 overflow-y-auto">
                {ALL_SET_TYPES.map(({ value, info }) => {
                  const isActive = currentType === value;
                  return (
                    <button key={String(value)} onClick={() => { handleSetChange(setTypePicker.peId, setTypePicker.setId, 'setType', value); setSetTypePicker(null); }}
                      title={info.description}
                      className={`px-3 py-1.5 rounded-sm border font-mono text-[10px] font-bold transition-all active:scale-95 ${
                        isActive ? `${info.colorClass} ${info.bgClass} ${info.borderClass} ring-1 ring-current` : `${info.colorClass} border-border/40 bg-transparent opacity-60 hover:opacity-100`
                      }`}>{info.shortLabel}</button>
                  );
                })}
              </div>
              {currentDesc && <p className="text-[10px] text-muted-foreground mt-2 font-mono border-t border-border/40 pt-2">{currentDesc}</p>}
            </div>
          </div>
        );
      })()}

      {/* Skip Exercise Dialog */}
      {skipDialog && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#1e1e1e] border border-orange-500/30 rounded-sm w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
              <span className="font-mono text-sm font-bold text-orange-400">⚡ SKIP EXERCISE</span>
              <button onClick={() => { setSkipDialog(null); setSkipReasonInput(''); }} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <p className="font-mono text-xs text-white/70">{skipDialog.exName} will be marked as skipped and noted in your session record.</p>
              <div className="space-y-1.5">
                <div className="text-[9px] font-mono text-white/40 uppercase tracking-widest">REASON</div>
                <div className="grid grid-cols-2 gap-1.5">
                  {['Too fatigued', 'No equipment', 'Out of time', 'Injury risk'].map(reason => (
                    <button
                      key={reason}
                      onClick={() => setSkipReasonInput(reason)}
                      className={`px-2 py-2 rounded-sm border font-mono text-[10px] transition-all active:scale-95 ${
                        skipReasonInput === reason
                          ? 'bg-orange-500/20 border-orange-500/60 text-orange-300'
                          : 'border-white/10 text-white/50 hover:border-orange-500/30 hover:text-orange-400'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Custom reason..."
                  value={['Too fatigued', 'No equipment', 'Out of time', 'Injury risk'].includes(skipReasonInput) ? '' : skipReasonInput}
                  onChange={(e) => setSkipReasonInput(e.target.value)}
                  className="w-full bg-[#2a2a2a] border border-white/10 rounded-sm px-3 py-2 font-mono text-xs text-white placeholder:text-white/30 focus:outline-none focus:border-orange-500/40"
                />
              </div>
            </div>
            <div className="flex gap-2 px-4 pb-4">
              <Button variant="outline" size="sm" onClick={() => { setSkipDialog(null); setSkipReasonInput(''); }} className="flex-1 font-mono text-xs">CANCEL</Button>
              <Button
                size="sm"
                onClick={() => handleSkipExercise(skipDialog.peId, skipReasonInput || 'Too fatigued')}
                className="flex-1 font-mono text-xs bg-orange-500/20 hover:bg-orange-500/30 text-orange-300 border border-orange-500/40"
              >
                SKIP IT
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Slide-Up Rest Drawer */}
      {showRestDrawer && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex flex-col justify-end">
          <div className="bg-card border-t border-border rounded-t-lg p-4 max-h-[85vh] flex flex-col shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <span className="font-mono text-sm font-bold text-primary flex items-center gap-1.5"><Timer className="h-4 w-4" /> REST STATION</span>
              <Button variant="ghost" size="sm" onClick={() => setShowRestDrawer(false)} className="font-mono text-xs">CLOSE</Button>
            </div>
            <div className="text-center py-6 bg-secondary/30 border border-border/60 rounded-sm flex flex-col items-center">
              <div className="relative w-36 h-36 flex items-center justify-center mb-4">
                <svg className="w-full h-full transform -rotate-90 absolute">
                  <circle cx="72" cy="72" r="60" className="stroke-white/15 fill-none" strokeWidth="5" />
                  <circle cx="72" cy="72" r="60"
                    className={`fill-none transition-all duration-300 ${timerActive && timerSeconds <= 5 ? 'stroke-destructive' : 'stroke-primary'}`}
                    strokeWidth="5"
                    strokeDasharray="376.99"
                    strokeDashoffset={timerActive && timerPreset > 0 ? 376.99 * (1 - timerSeconds / timerPreset) : 0}
                  />
                </svg>
                <div className="z-10 flex flex-col items-center">
                  <div className="font-mono text-4xl font-bold tracking-widest text-primary">
                    {Math.floor(timerSeconds / 60)}:{(timerSeconds % 60).toString().padStart(2, '0')}
                  </div>
                  <p className="text-xs font-mono uppercase text-muted-foreground mt-1">{timerActive ? 'RECOVERING' : 'TIMER INACTIVE'}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" onClick={() => { playBeep(550, 0.05); setTimerSeconds(prev => Math.max(0, prev - 15)); }} className="font-mono text-xs h-9 px-3">-15S</Button>
                <Button size="default" variant={timerActive ? 'destructive' : 'default'}
                  onClick={() => { if (timerActive) { setTimerActive(false); setTimerSeconds(0); } else handleStartRestTimer(); }}
                  className="font-mono text-xs h-9 px-6">{timerActive ? 'RESET' : 'START REST'}</Button>
                <Button size="sm" variant="outline" onClick={() => { playBeep(550, 0.05); setTimerSeconds(prev => prev + 15); }} className="font-mono text-xs h-9 px-3">+15S</Button>
              </div>
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-mono uppercase text-muted-foreground">Preset Rest Durations</label>
              <div className="grid grid-cols-5 gap-1">
                {[30, 60, 90, 120].map((secs) => (
                  <Button key={secs} variant={timerPreset === secs ? 'default' : 'outline'} size="sm"
                    onClick={() => { setTimerPreset(secs); if (timerActive) handleStartRestTimer(secs); }}
                    className="h-10 font-mono text-xs">{secs}s</Button>
                ))}
                {activeExercise && (
                  <Button variant={timerPreset === recommendedRest ? 'default' : 'outline'} size="sm"
                    onClick={() => { setTimerPreset(recommendedRest); if (timerActive) handleStartRestTimer(recommendedRest); }}
                    className="h-10 font-mono text-xs border-primary/40 text-primary bg-primary/5">{recommendedRest}s ★</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Exercise Library Modal */}
      {showLibrary && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between bg-secondary/20">
              <span className="font-mono text-sm font-bold text-primary flex items-center gap-1.5"><Dumbbell className="h-4 w-4" /> EXERCISE VAULT</span>
              <Button variant="ghost" size="sm" onClick={() => setShowLibrary(false)} className="font-mono text-xs">CANCEL</Button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              <ExerciseLibrary onSelectExercise={handleAddExerciseOnTheFly} showSelectButton={true} />
            </div>
          </div>
        </div>
      )}

      {/* Finish Workout Confirmation Dialog */}
      <Dialog open={showFinishDialog} onOpenChange={setShowFinishDialog}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="font-mono text-primary">🔥 FINISH WORKOUT</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              End session? You've completed {completedSetsCount} of {totalSetsCount} planned sets.
            </p>
            <div className="space-y-1.5">
              <label className="font-mono text-xs text-muted-foreground uppercase tracking-wider">Workout Duration (MM:SS)</label>
              <Input type="text" value={manualDurationInput} onChange={(e) => setManualDurationInput(e.target.value)} placeholder="e.g. 58:30"
                className="font-mono text-center text-lg h-12 bg-secondary border-border text-foreground"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowFinishDialog(false)} className="font-mono text-xs flex-1">KEEP GOING</Button>
            <Button onClick={handleConfirmFinish} disabled={saveSessionMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs flex-1">
              {saveSessionMutation.isPending ? 'SAVING...' : '✓ END & SAVE'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Plate Calculator Drawer */}
      {showPlateDrawer && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex flex-col justify-end">
          <div className="bg-card border-t border-border rounded-t-lg p-4 max-h-[85vh] flex flex-col shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <span className="font-mono text-sm font-bold text-primary flex items-center gap-1.5"><Dumbbell className="h-4 w-4" /> BARBELL PLATE CALCULATOR</span>
              <Button variant="ghost" size="sm" onClick={() => setShowPlateDrawer(false)} className="font-mono text-xs">CLOSE</Button>
            </div>
            <div className="overflow-y-auto pb-6"><BarbellPlateCalculator /></div>
          </div>
        </div>
      )}

      {/* Post-session feedback card */}
      {showFeedbackCard && completedSessionId && (() => {
        // Compute session stats for the summary card
        const allSets = session.plannedExercises.flatMap(pe => pe.sets);
        const workingSets = allSets.filter(s => !s.isChildBlock && s.setType !== 'feeder' && s.completed);
        const totalVolumeLbs = allSets
          .filter(s => s.completed && s.setType !== 'feeder')
          .reduce((acc, s) => acc + ((s.weight ?? 0) * (s.reps ?? 0)), 0);
        return (
          <PostSessionFeedbackCard
            sessionId={completedSessionId}
            sessionName={session.name}
            stats={{ totalSets: workingSets.length, totalVolumeLbs: Math.round(totalVolumeLbs), durationSeconds: elapsedSeconds }}
            skippedExercises={session.plannedExercises
              .filter(pe => pe.skipped)
              .map(pe => ({
                name: exercises.find(e => e.id === pe.exerciseId)?.name ?? 'Unknown',
                reason: pe.skipReason ?? 'Too fatigued'
              }))
            }
            onDone={(_feeling, _nudge) => { setShowFeedbackCard(false); onComplete(); }}
            onSkip={() => { setShowFeedbackCard(false); onComplete(); }}
          />
        );
      })()}
    </div>
  );
}
